<template>
  <div class="datagrid-container sm:py-4">
    <div v-if="loading" class="p-3 sm:p-4">
      <div
        class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6"
      >
        <Skeleton class="w-full sm:w-[350px]" height="80px" borderRadius="12px"></Skeleton>
      </div>

      <div
        class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 mb-3 sm:mb-4"
      >
        <Skeleton class="w-full sm:w-64" height="42px" borderRadius="8px"></Skeleton>
      </div>

      <div class="bg-(--p-surface-50) rounded-lg p-3 sm:p-4 overflow-x-auto">
        <div class="flex gap-2 sm:gap-3 mb-2 sm:mb-3 min-w-max sm:min-w-0">
          <Skeleton v-for="n in 5" :key="`header-${n}`" class="flex-1" height="60px"></Skeleton>
        </div>
        <div v-for="n in 8" :key="`row-${n}`" class="flex gap-2 sm:gap-3 mb-2 min-w-max sm:min-w-0">
          <Skeleton v-for="m in 5" :key="`cell-${n}-${m}`" class="flex-1" height="50px"></Skeleton>
        </div>
      </div>

      <div
        class="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 sm:gap-0 mt-3 sm:mt-4"
      >
        <div class="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          <Skeleton class="w-full sm:w-48" height="36px sm:40px" borderRadius="8px"></Skeleton>
          <Skeleton class="w-full sm:w-48" height="36px sm:40px" borderRadius="8px"></Skeleton>
        </div>
        <Skeleton class="w-full sm:w-64" height="36px sm:40px" borderRadius="8px"></Skeleton>
      </div>
    </div>
    <div v-else>
      <template v-if="!props.hideHeader">
        <div class="mb-4 sm:mb-6">
          <div
            class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4"
          >
            <div
              class="bg-gradient-to-br from-(--p-primary-500) to-(--p-primary-600) rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-5 text-white shadow-lg w-full sm:w-auto"
            >
              <div class="flex items-center gap-2 sm:gap-3">
                <div
                  class="w-7 h-7 sm:w-8 sm:h-8 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center text-2xl sm:text-3xl flex-shrink-0"
                >
                  <i class="pi pi-file-check text-white"></i>
                </div>
                <div class="flex flex-col min-w-0 flex-1">
                  <h3 class="text-base sm:text-lg lg:text-xl font-bold mb-0 sm:mb-1 truncate">
                    {{ reportTitle }} Report
                  </h3>
                </div>
              </div>
            </div>
            <div class="flex flex-wrap gap-2 items-center w-full sm:w-auto">
              <div
                class="report-info-card bg-gradient-to-br from-(--p-primary-500) to-(--p-primary-600) rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-5 text-white shadow-lg flex-1 sm:flex-none"
              >
                <div class="flex items-center text-xs opacity-95 whitespace-nowrap">
                  <i class="pi pi-calendar text-xs mr-1 text-white"></i>
                  <span class="truncate">Generated on {{ currentTime }}</span>
                </div>
              </div>
              <div
                class="report-info-card bg-gradient-to-br from-(--p-primary-500) to-(--p-primary-600) rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-5 text-white shadow-lg flex-1 sm:flex-none"
                v-if="filteredData?.length"
              >
                <div class="flex items-center text-xs opacity-95 whitespace-nowrap">
                  <i class="pi pi-list text-xs mr-1 text-white"></i>
                  <span
                    >{{ filteredData.length }}
                    {{ filteredData.length === 1 ? 'record' : 'records' }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Mobile Card View -->
      <div v-if="isMobile && filters && !loading" class="mobile-datagrid-wrapper">
        <!-- Mobile Export Buttons -->
        <div
          v-if="
            (getSetting('showDownloadDocument') || getSetting('showDownloadSpreadsheet')) &&
            !props.hideHeader
          "
          class="mobile-export-buttons mb-4 flex flex-col gap-2"
        >
          <div class="flex gap-2">
            <Button
              v-if="getSetting('showDownloadDocument')"
              icon="pi pi-file-pdf"
              label="Export PDF"
              severity="secondary"
              :disabled="!data || !data.length"
              @click="statementExportVisible = true"
              class="flex-1"
            />
            <Button
              v-if="getSetting('showDownloadSpreadsheet')"
              icon="pi pi-file-excel"
              label="Export Excel"
              severity="secondary"
              :disabled="!filteredData?.length"
              @click="exportCSV"
              class="flex-1"
            />
          </div>
          <div
            v-if="getSetting('showDownloadSpreadsheet') && getSetting('showDownloadDocument')"
            class="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700"
          >
            <i class="pi pi-info-circle text-base text-blue-600"></i>
            <span>Excel files are amendable; PDF documents contain confirmed data only.</span>
          </div>
        </div>

        <!-- Mobile Filters -->
        <div
          v-if="!props.hideHeader"
          class="mobile-filters mb-4 p-3 bg-(--p-surface-50) rounded-lg"
        >
          <div class="flex flex-col gap-2">
            <IconField>
              <InputIcon>
                <i class="pi pi-search" />
              </InputIcon>
              <InputText
                v-model="filters.global.value"
                placeholder="Search all columns..."
                class="w-full"
              />
            </IconField>
            <div class="flex gap-2">
              <MultiSelect
                v-if="getSetting('showMonthFilter') && isTrackerMonthlyConfig"
                v-model="filteredMonth"
                :options="monthPeriods"
                :placeholder="monthSelectorPlaceholder"
                filter
                filterPlaceholder="Search months..."
                display="chip"
                :maxSelectedLabels="0"
                class="flex-1"
              >
                <template #value="{ value, placeholder }">
                  <span v-if="!value || value.length === 0">{{ placeholder }}</span>
                  <span v-else
                    >{{ value.length }} month{{ value.length === 1 ? '' : 's' }} selected</span
                  >
                </template>
              </MultiSelect>
              <Select
                v-else-if="getSetting('showMonthFilter')"
                v-model="filteredMonth"
                :options="monthPeriods"
                placeholder="Month"
                filter
                filterPlaceholder="Search..."
                class="flex-1"
              />
              <Select
                v-if="getSetting('showYearFilter')"
                v-model="filteredYear"
                :options="['2025', '2026']"
                placeholder="Year"
                class="flex-1"
              />
            </div>
            <Button
              v-if="activeFiltersCount > 0"
              type="button"
              icon="pi pi-filter-slash"
              label="Clear Filters"
              severity="secondary"
              outlined
              size="small"
              @click="clearFilter()"
              class="w-full"
            />
            <div
              v-if="props.type === 'VAT Notes' && isVatNotesQuarterlyPeriod"
              class="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700"
            >
              <i class="pi pi-info-circle text-base text-amber-600 mt-0.5"></i>
              <span>
                Quarterly report for {{ vatNotesPeriodLabel }}. Data is aggregated across the
                quarter.
              </span>
            </div>
          </div>
        </div>

        <!-- Mobile Cards -->
        <div class="mobile-cards space-y-3">
          <div
            v-for="row in paginatedMobileData"
            :key="getRowId(row)"
            class="mobile-card bg-white rounded-lg shadow-sm border border-(--p-surface-200) overflow-hidden transition-all"
            :class="{ expanded: expandedCardIds.includes(getRowId(row)) }"
          >
            <!-- Card Header (Always Visible) -->
            <div
              class="card-header p-4 cursor-pointer active:bg-(--p-surface-50)"
              @click="toggleCardExpansion(getRowId(row))"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="flex-1 min-w-0">
                  <!-- Primary Identifier -->
                  <h4 class="text-base font-semibold text-(--p-anthracite-500) mb-2 truncate">
                    {{ getPrimaryFieldValue(row) }}
                  </h4>

                  <!-- Key Metrics Row -->
                  <div class="flex flex-wrap gap-3">
                    <div
                      v-for="metric in getKeyMetrics(row)"
                      :key="metric.label"
                      class="metric-item"
                    >
                      <span class="text-xs text-(--p-anthracite-500) block mb-0.5">
                        {{ metric.label }}
                      </span>
                      <span class="text-sm font-bold" :class="metric.class">
                        {{ metric.value }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Expand/Collapse Icon -->
                <div class="flex-shrink-0">
                  <i
                    :class="
                      expandedCardIds.includes(getRowId(row))
                        ? 'pi pi-chevron-up'
                        : 'pi pi-chevron-down'
                    "
                    class="text-(--p-anthracite-500) text-xl"
                  ></i>
                </div>
              </div>
            </div>

            <!-- Card Details (Expandable) -->
            <Transition name="slide-down">
              <div
                v-if="expandedCardIds.includes(getRowId(row))"
                class="card-details border-t border-(--p-surface-200) p-4 space-y-2.5"
              >
                <div
                  v-for="column in visibleColumns"
                  :key="getColumnKey(column)"
                  class="detail-row flex flex-col gap-2 py-2 border-b border-(--p-surface-100) last:border-0"
                >
                  <span class="detail-label text-sm font-bold text-(--p-anthracite-500)">
                    {{ column.header }}
                  </span>
                  <span
                    class="detail-value text-sm text-(--p-anthracite-500) break-words"
                    :class="getCellClass(row, column)"
                  >
                    {{ formatCellValueForMobile(row, column) || '—' }}
                  </span>
                </div>
              </div>
            </Transition>
          </div>

          <!-- Empty State -->
          <div v-if="!filteredData || filteredData.length === 0" class="text-center py-12 px-4">
            <i class="pi pi-inbox !text-4xl text-(--p-anthracite-500) mb-3"></i>
            <h4 class="text-base font-bold text-(--p-anthracite-500) mb-2">No Data Available</h4>
            <p class="text-xs text-(--p-anthracite-500)">
              No records match your current filters. Try adjusting your search criteria.
            </p>
          </div>
        </div>

        <!-- Mobile Pagination -->
        <div
          v-if="filteredData && filteredData.length > 0"
          class="mobile-pagination mt-4 p-3 bg-(--p-surface-50) rounded-lg"
        >
          <div class="flex items-center justify-between gap-2">
            <div class="text-xs text-(--p-anthracite-500)">
              Showing {{ mobilePageStart }} to {{ mobilePageEnd }} of {{ filteredData.length }}
            </div>
            <div class="flex items-center gap-2">
              <Button
                icon="pi pi-chevron-left"
                size="small"
                text
                :disabled="mobileCurrentPage === 1"
                @click="mobileCurrentPage--"
                aria-label="Previous page"
              />
              <span class="text-sm text-(--p-anthracite-500) px-2">
                {{ mobileCurrentPage }} / {{ mobileTotalPages }}
              </span>
              <Button
                icon="pi pi-chevron-right"
                size="small"
                text
                :disabled="mobileCurrentPage >= mobileTotalPages"
                @click="mobileCurrentPage++"
                aria-label="Next page"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop Table View -->
      <DataTable
        v-if="filters && !isMobile"
        ref="datatable"
        v-model:filters="filters"
        :value="filteredData"
        paginator
        showGridlines
        resizableColumns
        :rows="15"
        dataKey="id"
        filterDisplay="menu"
        :globalFilterFields="globalFilters"
        stripedRows
        :rowHover="true"
        :rowClass="getRowClass"
        @row-click="handleRowClick"
        class="custom-datagrid responsive-datatable rounded-lg overflow-hidden"
        :paginatorTemplate="'RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink'"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        :rowsPerPageOptions="[10, 15, 25, 50, 100]"
        :sortField="getSortField()"
        :sortOrder="getSortOrder()"
      >
        <template #header v-if="!props.hideHeader">
          <div class="bg-(--p-surface-50) p-3 sm:p-4 rounded-lg mb-2">
            <div class="flex flex-col sm:flex-row sm:justify-between gap-3 sm:gap-4">
              <div class="flex flex-wrap items-center gap-2">
                <template v-if="getSetting('showMonthFilter')">
                  <div class="flex flex-col gap-1.5 w-full sm:w-auto">
                    <MultiSelect
                      v-if="isTrackerMonthlyConfig"
                      v-model="filteredMonth"
                      :options="monthPeriods"
                      :placeholder="monthSelectorPlaceholder"
                      filter
                      filterPlaceholder="Search months..."
                      display="chip"
                      :maxSelectedLabels="0"
                      class="w-full sm:min-w-[180px]"
                    >
                      <template #value="{ value, placeholder }">
                        <span v-if="!value || value.length === 0">{{ placeholder }}</span>
                        <span v-else
                          >{{ value.length }} month{{
                            value.length === 1 ? '' : 's'
                          }}
                          selected</span
                        >
                      </template>
                    </MultiSelect>
                    <Select
                      v-else
                      v-model="filteredMonth"
                      :options="monthPeriods"
                      placeholder="Select Month"
                      filter
                      filterPlaceholder="Search months..."
                      class="w-full sm:min-w-[180px]"
                    />
                  </div>
                </template>
                <template v-if="getSetting('showYearFilter')">
                  <div class="flex flex-col gap-1.5 w-full sm:w-auto">
                    <label
                      class="text-xs font-semibold text-(--p-anthracite-500) flex items-center uppercase tracking-wide"
                    >
                      <i class="pi pi-calendar mr-1"></i>
                      Year
                    </label>
                    <Select
                      v-model="filteredYear"
                      :options="['2025', '2026']"
                      placeholder="Select Year"
                      class="w-full sm:min-w-[180px]"
                    />
                  </div>
                </template>
                <Button
                  v-if="!isMobile"
                  type="button"
                  icon="pi pi-filter-slash"
                  label="Clear"
                  severity="secondary"
                  outlined
                  size="small"
                  @click="clearFilter()"
                  v-tooltip.top="'Clear all filters'"
                  class="w-full sm:w-auto"
                />
                <Badge
                  v-if="activeFiltersCount > 0 && !isMobile"
                  :value="activeFiltersCount"
                  severity="info"
                  v-tooltip.top="'Active filters'"
                  class="flex-shrink-0 !bg-(--p-primary-500) !text-white"
                />
              </div>

              <div class="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div class="w-full sm:min-w-[280px] sm:flex-1">
                  <IconField>
                    <InputIcon>
                      <i class="pi pi-search" />
                    </InputIcon>
                    <InputText
                      v-model="filters.global.value"
                      placeholder="Search all columns..."
                      class="w-full"
                    />
                  </IconField>
                </div>
                <Button
                  v-if="getSetting('showColumnSelect') && !isMobile"
                  ref="columnVisibilityButton"
                  type="button"
                  icon="pi pi-list"
                  severity="secondary"
                  outlined
                  size="small"
                  @click="toggleColumnVisibilityPopover"
                  v-tooltip.top="'Show/Hide Columns'"
                  class="w-full sm:w-auto"
                />
              </div>
            </div>
          </div>
          <div
            v-if="props.type === 'VAT Notes' && isVatNotesQuarterlyPeriod"
            class="mt-3 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700"
          >
            <i class="pi pi-info-circle text-base text-amber-600 mt-0.5"></i>
            <span>
              Quarterly report for {{ vatNotesPeriodLabel }}. Data is aggregated across the quarter.
            </span>
          </div>
        </template>

        <template #empty>
          <div class="py-12 sm:py-16 px-4 sm:px-8 text-center">
            <i class="pi pi-inbox !text-4xl sm:!text-5xl text-(--p-anthracite-500) mb-3 sm:mb-4"></i>
            <h4 class="text-base sm:text-lg font-bold text-(--p-anthracite-500) m-0 mb-2">
              No Data Available
            </h4>
            <p class="text-xs sm:text-sm text-(--p-anthracite-500) m-0 max-w-md mx-auto px-2">
              No records match your current filters. Try adjusting your search criteria.
            </p>
          </div>
        </template>

        <Column
          v-for="(column, index) in visibleColumns"
          v-bind:key="index"
          :field="getColumnField(column)"
          :header="column.header"
          :filterField="getColumnField(column)"
          :dataType="column.type === 'date' ? 'date' : undefined"
          sortable
          :class="{ 'bg-(--p-primary-50)': index % 2 }"
        >
          <template #body="{ data }">
            <template v-if="column.type == 'date'">
              {{ data[column.field] ? formatDate(data[column.field]) : '' }}
            </template>
            <template v-else-if="column.type == 'percent'"> {{ data[column.field] }}% </template>
            <template v-else-if="column.type == 'currency'">
              <span
                :class="{
                  'text-red-600': data[column.field] && data[column.field] < 0 && !isMaiaRecharge,
                  'text-black': data[column.field] && data[column.field] < 0 && isMaiaRecharge,
                }"
              >
                {{ data[column.field] ? formatCurrencyValue(data[column.field]) : '£0.00' }}
              </span>
            </template>
            <template v-else>
              <template v-if="Array.isArray(column.field)">
                <template v-for="(field, fieldIndex) in column.field" :key="fieldIndex">
                  <template v-if="field == 'total'">
                    <span
                      :class="{
                        'text-red-600': data[field] && data[field] < 0 && !isMaiaRecharge,
                        'text-black': data[field] && data[field] < 0 && isMaiaRecharge,
                      }"
                    >
                      {{ data[field] ? formatCurrencyValue(data[field]) || '£0.00' : '£0.00' }}
                    </span>
                    <span v-if="data[field]" :key="`space-${fieldIndex}`"> </span>
                  </template>
                  <template v-else>
                    {{ data[field] ? decodeHtmlEntities(String(data[field])) + ' ' : '' }}
                  </template>
                </template>
              </template>
              <template v-else>
                <template v-if="column.field === 'agreement_reference'">
                  {{ formatAgreementReference(data[column.field]) }}
                </template>
                <template v-else>
                  {{ data[column.field] ? decodeHtmlEntities(String(data[column.field])) : '' }}
                </template>
              </template>
            </template>
          </template>
          <template v-if="column.filter" #filter="{ filterModel }">
            <template v-if="column.type == 'text'">
              <InputText v-model="filterModel.value" type="text" placeholder="Search by name" />
            </template>
            <template v-if="column.type == 'currency'">
              <InputNumber
                v-model="filterModel.value"
                mode="currency"
                currency="USD"
                locale="en-US"
              />
            </template>
            <template v-if="column.type == 'date'">
              <DatePicker
                v-model="filterModel.value"
                dateFormat="dd/mm/yy"
                placeholder="dd/mm/yyyy"
              />
            </template>
          </template>
        </Column>

        <template #paginatorstart>
          <div class="flex flex-col gap-4">
            <div class="flex gap-3 flex-wrap">
              <Button
                v-if="getSetting('showDownloadDocument')"
                icon="pi pi-file-pdf"
                label="Export PDF"
                severity="secondary"
                :disabled="!data || !data.length"
                @click="statementExportVisible = true"
                v-tooltip.top="'Download as PDF document'"
                class="!border-1 !border-(--p-primary-500)"
              />
              <Button
                v-if="getSetting('showDownloadSpreadsheet')"
                icon="pi pi-file-excel"
                label="Export Excel"
                severity="secondary"
                :disabled="!filteredData?.length"
                @click="exportCSV"
                v-tooltip.top="'Download as Excel spreadsheet'"
                class="!border-1 !border-(--p-primary-500)"
              />
            </div>
            <div
              v-if="getSetting('showDownloadSpreadsheet') && getSetting('showDownloadDocument')"
              class="flex items-center gap-2.5 px-4 py-3 bg-(--p-primary-500) border border-(--p-primary-500) rounded-lg text-xs text-white"
            >
              <i class="pi pi-info-circle text-base text-white"></i>
              <span>Excel files are amendable; PDF documents contain confirmed data only.</span>
            </div>
          </div>
        </template>
      </DataTable>
    </div>

    <Popover
      ref="columnVisibilityPopover"
      :dismissable="true"
      class="column-visibility-popover"
    >
      <div class="flex flex-col gap-3 w-full sm:min-w-[320px] sm:max-w-[400px]">
        <div
          class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 pb-3 border-b border-surface-200"
        >
          <div class="flex items-center gap-2">
            <i class="pi pi-list text-base sm:text-lg text-primary-600"></i>
            <h4 class="text-sm sm:text-base font-bold m-0 text-surface-900">Columns</h4>
          </div>
          <div class="flex gap-1 w-full sm:w-auto">
            <Button
              label="Show All"
              size="small"
              text
              severity="secondary"
              @click="showAllColumns"
              v-tooltip.top="'Show all columns'"
              class="flex-1 sm:flex-none"
            />
            <Button
              label="Hide All"
              size="small"
              text
              severity="secondary"
              @click="hideAllColumns"
              v-tooltip.top="'Hide all columns'"
              class="flex-1 sm:flex-none"
            />
          </div>
        </div>

        <div class="mb-2">
          <IconField>
            <InputIcon>
              <i class="pi pi-search" />
            </InputIcon>
            <InputText
              v-model="columnSearchQuery"
              placeholder="Search columns..."
              class="w-full"
              size="small"
            />
          </IconField>
        </div>

        <div class="flex flex-col gap-1 max-h-[350px] overflow-y-auto pr-1">
          <div
            v-for="(column, index) in filteredColumns"
            :key="index"
            class="flex items-center gap-3 p-2.5 rounded-lg hover:bg-primary-50 transition-all cursor-pointer group"
            @click="toggleColumnVisibility(column)"
          >
            <Checkbox
              :inputId="`col-${index}`"
              :binary="true"
              :modelValue="columnVisibility[getColumnKey(column)]"
              @update:modelValue="(value) => updateColumnVisibilityForColumn(column, value)"
              @click.stop
            />
            <label
              :for="`col-${index}`"
              class="flex-1 cursor-pointer text-sm font-medium text-surface-700 group-hover:text-primary-700 transition-colors"
            >
              {{ column.header }}
            </label>
            <i
              v-if="columnVisibility[getColumnKey(column)]"
              class="pi pi-eye text-primary-600 text-xs"
            ></i>
            <i v-else class="pi pi-eye-slash text-surface-400 text-xs"></i>
          </div>
          <div
            v-if="filteredColumns.length === 0"
            class="text-center py-6 text-sm text-surface-500"
          >
            <i class="pi pi-search text-2xl mb-2 block"></i>
            <p class="m-0">No columns found</p>
          </div>
        </div>

        <div
          class="pt-2 border-t border-surface-200 flex items-center justify-between text-xs text-surface-500"
        >
          <span>{{ visibleColumnsCount }} of {{ props.config.columns?.length || 0 }} visible</span>
          <Button
            label="Reset"
            size="small"
            text
            severity="secondary"
            @click="resetColumnVisibility"
            v-tooltip.top="'Reset to default'"
          />
        </div>
      </div>
    </Popover>

    <Dialog
      v-if="!loading && data && data.length"
      v-model:visible="statementExportVisible"
      modal
      :header="`Download ${type} Statement`"
      class="export-dialog rounded-xl"
      :style="{ width: '80vw', maxWidth: '1200px' }"
    >
      <template #header>
        <div class="flex items-center gap-3">
          <i class="pi pi-file-pdf !text-2xl" :class="isMaiaRecharge ? 'text-black' : 'text-red-600'"></i>
          <div class="flex flex-col">
            <h3 class="text-lg font-bold">Download {{ type }} Statement</h3>
            <p class="text-sm text-(--p-anthracite-500)">Preview and download your report as PDF</p>
          </div>
        </div>
      </template>

      <div class="flex justify-end gap-3 mb-4">
        <Button
          type="button"
          label="Download PDF"
          icon="pi pi-download"
          severity="secondary"
          @click="exportPDF"
          :loading="exportGenerating"
          :disabled="exportGenerating"
        />
      </div>
      <div
        ref="statementExport"
        :class="['bg-white text-black font-sans p-0 statement-export-container', { 'maia-recharge': isMaiaRecharge }]"
      >
        <div
          class="bg-[var(--p-primary-500)] text-white p-5 flex justify-between items-center mb-5 statement-header"
        >
          <div class="flex flex-col">
            <h1 class="text-2xl font-bold m-0 flex-1 statement-title">
              {{
                props.type === 'MAIA Recharge' ? 'MAIA Recharge Invoice' : `${props.type} Statement`
              }}
            </h1>
            <p class="text-xs m-0 flex-1 text-right statement-address">
              Stellantis Financial Services UK Ltd, 61 London Road, Redhill, Surrey, RH1 1QA
            </p>
          </div>
          <img
            src="/stellantis-logo.png"
            alt="Stellantis Logo"
            class="max-w-[180px] ml-5 statement-logo"
          />
        </div>

        <div class="px-2.5 retailer-info-section">
          <template v-if="props.type === 'VAT Notes' || props.type === 'MAIA Recharge'">
            <div class="flex gap-8 vat-notes-retailer-info">
              <div class="flex-1">
                <div class="font-bold text-sm mb-1">From:</div>
                <template v-if="props.type === 'MAIA Recharge'">
                  <div class="text-sm mb-1">Retailer Code: {{ props.retailer?.bank_code }}</div>
                </template>
                <div class="text-sm mb-1">{{ props.retailer?.label }}</div>
                <div class="text-sm mb-1">{{ formatRetailerAddress(props.retailer) }}</div>
                <div class="text-sm mb-1">VAT No: {{ props.retailer?.vat_number || '' }}</div>
                <template v-if="props.type === 'VAT Notes'">
                  <div class="text-sm">Invoice No: {{ vatInvoiceNumber }}</div>
                </template>
                <template v-if="latestInvoiceDate">
                  <div class="text-sm">Invoice Date: {{ formatDate(latestInvoiceDate, 'long') }}</div>
                </template>
              </div>
              <div class="flex-1 text-right">
                <div class="font-bold text-sm mb-1">To:</div>
                <div class="text-sm mb-1">Stellantis Financial Services UK LTD</div>
                <div class="text-sm mb-1">61 London Road, Redhill, Surrey, RH1 1QA</div>
                <div class="text-sm mb-1">VAT No: 198087162</div>
                <template v-if="showVatNotesPartnershipHeading">
                  <div class="text-sm">Partnership Bonus Incentive: {{ vatQuarterYear }}</div>
                </template>
              </div>
            </div>
          </template>
          <template v-else-if="props.type === 'Insurance'">
            <div class="flex gap-8 vat-notes-retailer-info">
              <div class="flex-1">
                <div class="text-sm mb-1">{{ props.retailer?.label }}</div>
                <div class="text-sm mb-1">{{ props.retailer?.bank_code }}</div>
                <div class="text-sm mb-1">{{ formatRetailerAddress(props.retailer) }}</div>
                <div v-if="statementPeriod" class="text-sm mb-1">
                  Statement Period: {{ statementPeriod }}
                </div>
                <div class="text-sm">
                  Opening Balance: {{ formatCurrencyValue(insuranceOpeningBalance) }}
                </div>
              </div>
              <div class="flex-1 text-right">
                <div class="text-sm mb-1">GardX Assure Ltd</div>
                <div class="text-sm mb-1">7 Clovelly Business Park</div>
                <div class="text-sm mb-1">Southbourne</div>
                <div class="text-sm mb-1">Emsworth</div>
                <div class="text-sm">PO10 8PE</div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="font-bold text-sm mb-1.5 retailer-code-label">
              {{ props.retailer ? 'RETAILER CODE' : 'GROUP CODE' }}:
              {{ props.retailer?.bank_code || props.group?.code }}
            </div>
            <div class="text-sm retailer-name">
              {{ props.retailer?.label || props.group?.label }}
            </div>

            <div v-if="props.type === 'Commission'" class="text-sm mt-1.5 opening-balance">
              Opening Balance: {{ formatCurrencyValue(commissionOpeningBalance) }}
            </div>
          </template>
        </div>

        <DataTable
          :value="exportTableData"
          showGridlines
          dataKey="id"
          class="my-5 w-full statement-table"
          :pt="{
            table: { style: 'border-collapse: collapse; width: 100%;' },
            thead: { style: 'display: table-header-group;' },
          }"
          :sortField="getSortField()"
          :sortOrder="getSortOrder()"
          :rowClass="getExportRowClass"
        >
          <Column
            v-for="(column, index) in visibleColumns"
            v-bind:key="index"
            :field="getColumnField(column)"
            :header="column.header"
            :class="{ 'bg-(--p-primary-50)': index % 2 }"
          >
            <template #body="{ data: rowData }">
              <template v-if="rowData.is_totals_row">
                <template v-if="column.type == 'currency'">
                  <span
                    :class="{
                      'text-red-600':
                        rowData[column.field] !== null &&
                        rowData[column.field] !== undefined &&
                        rowData[column.field] < 0 &&
                        !isMaiaRecharge,
                      'text-black':
                        rowData[column.field] !== null &&
                        rowData[column.field] !== undefined &&
                        rowData[column.field] < 0 &&
                        isMaiaRecharge,
                      'font-bold': true,
                    }"
                  >
                    {{
                      rowData[column.field] !== null && rowData[column.field] !== undefined
                        ? formatCurrencyValue(rowData[column.field])
                        : ''
                    }}
                  </span>
                </template>
                <template v-else-if="Array.isArray(column.field)">
                  <template v-for="(field, fieldIndex) in column.field" :key="fieldIndex">
                    <template v-if="field == 'total'">
                      <span
                        :class="{
                          'text-red-600':
                            rowData[field] !== null &&
                            rowData[field] !== undefined &&
                            rowData[field] < 0 &&
                            !isMaiaRecharge,
                          'text-black':
                            rowData[field] !== null &&
                            rowData[field] !== undefined &&
                            rowData[field] < 0 &&
                            isMaiaRecharge,
                          'font-bold': true,
                        }"
                      >
                        {{
                          rowData[field] !== null && rowData[field] !== undefined
                            ? formatCurrencyValue(rowData[field])
                            : '£0.00'
                        }}
                      </span>
                      <span
                        v-if="rowData[field] !== null && rowData[field] !== undefined"
                        :key="`space-${fieldIndex}`"
                      >
                      </span>
                    </template>
                    <template v-else>
                      {{ rowData[field] ? decodeHtmlEntities(String(rowData[field])) + ' ' : '' }}
                    </template>
                  </template>
                </template>
                <template v-else>
                  <span
                    :class="{
                      'font-bold': true,
                    }"
                  >
                    {{
                      rowData[column.field] ? decodeHtmlEntities(String(rowData[column.field])) : ''
                    }}
                  </span>
                </template>
              </template>
              <template v-else>
                <template v-if="column.type == 'date'">
                  {{ rowData[column.field] ? formatDateForTable(rowData[column.field]) : '' }}
                </template>
                <template v-else-if="column.type == 'percent'">
                  {{ rowData[column.field] ? rowData[column.field] + '%' : '' }}
                </template>
                <template v-else-if="column.type == 'currency'">
                  <span
                    :class="{
                      'text-red-600':
                        rowData[column.field] && rowData[column.field] < 0 && !isMaiaRecharge,
                      'text-black':
                        rowData[column.field] && rowData[column.field] < 0 && isMaiaRecharge,
                    }"
                  >
                    {{
                      rowData[column.field] ? formatCurrencyValue(rowData[column.field]) : '£0.00'
                    }}
                  </span>
                </template>
                <template v-else>
                  <template v-if="Array.isArray(column.field)">
                    <template v-for="(field, fieldIndex) in column.field" :key="fieldIndex">
                      <template v-if="field == 'total'">
                        <span
                          :class="{
                            'text-red-600': rowData[field] && rowData[field] < 0 && !isMaiaRecharge,
                            'text-black': rowData[field] && rowData[field] < 0 && isMaiaRecharge,
                          }"
                        >
                          {{ rowData[field] ? formatCurrencyValue(rowData[field]) : '£0.00' }}
                        </span>
                        <span v-if="rowData[field]" :key="`space-${fieldIndex}`"> </span>
                      </template>
                      <template v-else>
                        {{ rowData[field] ? rowData[field] + ' ' : '' }}
                      </template>
                    </template>
                  </template>
                  <template v-else>
                    <template v-if="column.field === 'agreement_reference'">
                      {{ formatAgreementReference(rowData[column.field]) }}
                    </template>
                    <template v-else-if="column.field === 'sub_dealer_code'">
                      {{
                        rowData[column.field]
                          ? decodeHtmlEntities(String(rowData[column.field]))
                          : ''
                      }}
                    </template>
                    <template v-else>
                      {{
                        rowData[column.field]
                          ? decodeHtmlEntities(String(rowData[column.field]))
                          : ''
                      }}
                    </template>
                  </template>
                </template>
              </template>
            </template>
          </Column>
        </DataTable>

        <div
          v-if="props.type === 'Commission' && filteredData.length > 0 && !statementExportVisible"
          class="mt-5 py-2.5 border-t-2 border-gray-800 totals-section"
        >
          <div class="totals-row flex justify-end font-bold text-[13px]">
            <div class="flex gap-3 justify-between w-[400px]">
              <span class="totals-label text-(--p-primary-500)">TOTALS</span>
              <div class="flex gap-5">
                <span v-if="hasColumn('funded_amount')" class="totals-value">{{
                  formatCurrencyValue(commissionTotals.amountFinanced)
                }}</span>
                <span v-if="hasColumn('scale_commission')" class="totals-value">{{
                  formatCurrencyValue(commissionTotals.scale)
                }}</span>
                <span v-if="hasColumn('fixed_commission')" class="totals-value">{{
                  formatCurrencyValue(commissionTotals.fixed)
                }}</span>
                <span class="totals-value">{{ formatCurrencyValue(commissionTotals.total) }}</span>
              </div>
            </div>
          </div>
          <div class="commission-paid text-[13px] font-bold flex justify-end mt-2">
            <div class="flex gap-3 justify-between w-[400px]">
              <span class="text-(--p-primary-500) mr-5">COMMISSION PAID:</span>
              <span class="inline-block">{{ formatCurrencyValue(advanceCommissionTotal) }}</span>
            </div>
          </div>
          <div
            class="closing-balance text-[13px] font-bold flex justify-end mt-2 text-(--p-primary-500)"
          >
            <div class="flex gap-3 justify-between w-[400px]">
              <span class="mr-5">CLOSING BALANCE:</span>
              <span class="inline-block">{{ formatCurrencyValue(commissionClosingBalance) }}</span>
            </div>
          </div>
        </div>

        <div
          v-if="
            (props.type === 'VAT Notes' || props.type === 'MAIA Recharge') && statementExportVisible
          "
          class="mt-5 py-2.5 text-[11px] text-(--p-anthracite-500) text-right"
        >
          <p class="m-0">The VAT shown is your output tax due to HMRC</p>
        </div>

        <div class="mt-2.5 px-2.5 py-4 border-t-2 border-gray-800 text-[11px] statement-footer">
          <div
            v-if="props.type === 'Commission'"
            class="flex flex-col gap-1 leading-3 mb-2 reference-details"
          >
            <span class="font-bold">Reference Details</span>
            <span
              >DB: DebitBalance | CR: Credit Balance | ADV: Advance | ADJ: Adjustment | ES: Early
              Settlement | RESC: Reschedule | CN: Cancellation | HT: Hostile Termination | VT:
              Voluntary Termination</span
            >
          </div>
          <div class="flex justify-between footer-info">
            <span>Printed: {{ formatPrintedDate(currentTime) }}</span>
            <span class="page-info"
              >Page <span class="page-number">1</span> of <span class="total-pages">1</span></span
            >
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue'
import axios from 'axios'
import html2pdf from 'html2pdf.js'
import { FilterMatchMode } from '@primevue/core/api'
import type GridColumn from '@/config/types/GridColumn'
import { FinancedDealsConfiguration } from '@/config/grids/FinancedDealsConfiguration'
import { AcceptedNotFundedConfiguration } from '@/config/grids/AcceptedNotFundedConfiguration'
import { TrackerDrilldownConfiguration } from '@/config/grids/TrackerDrilldownConfiguration'
import * as XLSX from '@e965/xlsx'

const props = defineProps([
  'config',
  'type',
  'scope',
  'retailer',
  'group',
  'dataKey',
  'showDataBoxes',
  'includeProposedNotFunded',
  'hideHeader',
  'enableTrackerExtras',
  'presetPeriod',
  'presetYear',
])
const data = ref()
const filters = ref()
const filterRef = ref()
const globalFilters = ref()
const loading = ref(true)
const isInitializing = ref(true)
const hasInitialLoad = ref(false)
const windowWidth = ref(window.innerWidth)

const isMobile = computed(() => windowWidth.value < 640)

// Mobile card view state
const expandedCardIds = ref<Array<string | number>>([])
const mobileCurrentPage = ref(1)
const mobileRowsPerPage = 10

// Update window width on resize
const handleResize = () => {
  windowWidth.value = window.innerWidth
}

if (typeof window !== 'undefined') {
  window.addEventListener('resize', handleResize)
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

const getCurrentMonthYear = () => {
  const today = new Date()
  return `${MONTHS[today.getMonth()]} ${today.getFullYear()}`
}

const getQuarterFromMonth = (monthName: string): string => {
  const monthIndex = MONTHS.indexOf(monthName as (typeof MONTHS)[number])
  if (monthIndex === -1) return ''
  if (monthIndex >= 0 && monthIndex <= 2) return 'Q1'
  if (monthIndex >= 3 && monthIndex <= 5) return 'Q2'
  if (monthIndex >= 6 && monthIndex <= 8) return 'Q3'
  return 'Q4'
}

const VAT_NOTES_FORMAT_CUTOFF = new Date(2024, 10, 1)
const VAT_NOTES_QUARTER_START = new Date(2024, 10, 1)
const VAT_NOTES_QUARTER_END = new Date(2025, 8, 1)
const VAT_NOTES_PARTNERSHIP_HEADING_CUTOFF = new Date(2025, 0, 1)

const parseMonthYear = (value: string): Date | null => {
  const parts = value.split(' ')
  if (parts.length !== 2) return null
  const monthIndex = MONTHS.indexOf(parts[0] as (typeof MONTHS)[number])
  if (monthIndex === -1) return null
  const year = Number(parts[1])
  if (Number.isNaN(year)) return null
  return new Date(year, monthIndex, 1)
}

const getQuarterLabel = (date: Date): string => {
  const quarter = Math.floor(date.getMonth() / 3) + 1
  return `Q${quarter} ${date.getFullYear()}`
}

const getQuarterPeriodLabel = (date: Date): string => {
  const quarter = Math.floor(date.getMonth() / 3) + 1
  const startMonthIndex = (quarter - 1) * 3
  const endMonthIndex = startMonthIndex + 2
  return `Q${quarter} ${date.getFullYear()} (${MONTHS[startMonthIndex]}-${MONTHS[endMonthIndex]})`
}

const vatInvoiceNumber = computed(() => {
  const monthValue = Array.isArray(filteredMonth.value)
    ? filteredMonth.value[0]
    : filteredMonth.value
  if (!monthValue || !props.retailer?.bank_code) return ''
  const parts = monthValue.split(' ')
  if (parts.length !== 2) return ''
  const year = parts[1]
  const monthName = parts[0]
  const quarter = getQuarterFromMonth(monthName)
  return `${year}${quarter}${props.retailer.bank_code}`
})

const vatQuarterYear = computed(() => {
  const monthValue = Array.isArray(filteredMonth.value)
    ? filteredMonth.value[0]
    : filteredMonth.value
  if (!monthValue) return ''
  const parts = monthValue.split(' ')
  if (parts.length !== 2) return ''
  const year = parts[1]
  const monthName = parts[0]
  const quarter = getQuarterFromMonth(monthName)
  return `${quarter} ${year}`
})

const latestInvoiceDate = computed(() => {
  if (!Array.isArray(data.value)) return ''

  return data.value
    .map((row: DataRow) => row.invoice_date)
    .filter((date): date is string => typeof date === 'string' && date.length > 0)
    .sort()
    .at(-1) || ''
})

const isTrackerMonthlyConfig = computed(
  () =>
    props.type === 'Performance Bonus Monthly' ||
    props.type === 'Performance Bonus Monthly Drilldown',
)
const isMaiaRecharge = computed(() => props.type === 'MAIA Recharge')

const filteredMonth = ref<string | string[]>(
  isTrackerMonthlyConfig.value ? [getCurrentMonthYear()] : getCurrentMonthYear(),
)

const monthSelectorPlaceholder = computed(() => {
  if (!isTrackerMonthlyConfig.value) return 'Select Months'
  const selectedCount = Array.isArray(filteredMonth.value) ? filteredMonth.value.length : 0
  if (selectedCount === 0) return 'Select Months'
  return `${selectedCount} month${selectedCount === 1 ? '' : 's'} selected`
})
const filteredYear = ref(new Date().getFullYear().toString())
const currentTime = ref('')

const reportTitle = computed(() => {
  if (props.type === 'Performance Bonus') {
    return 'Performance Tracking'
  }
  if (props.type === 'Performance Bonus Monthly') {
    return 'Monthly Used Tracking'
  }
  if (props.type === 'Performance Bonus Monthly Drilldown') {
    return 'Performance Tracking Drilldown'
  }
  return props.type
})

// Initialize filteredMonth from presetPeriod if provided, otherwise use default
if (props.presetPeriod) {
  if (isTrackerMonthlyConfig.value) {
    if (Array.isArray(props.presetPeriod)) {
      filteredMonth.value = props.presetPeriod
    } else if (typeof props.presetPeriod === 'string') {
      filteredMonth.value = [props.presetPeriod]
    }
  } else {
    filteredMonth.value = typeof props.presetPeriod === 'string' ? props.presetPeriod : ''
  }
}

// Watch for presetPeriod changes (including when it changes from null to a value)
watch(
  () => props.presetPeriod,
  (value) => {
    if (value !== null && value !== undefined) {
      if (isTrackerMonthlyConfig.value) {
        if (Array.isArray(value)) {
          filteredMonth.value = value.length > 0 ? value : [getCurrentMonthYear()]
        } else if (typeof value === 'string') {
          filteredMonth.value = [value]
        }
      } else {
        filteredMonth.value = typeof value === 'string' ? value : ''
      }
    }
  },
  { immediate: true },
)

if (props.presetYear && typeof props.presetYear === 'string') {
  filteredYear.value = props.presetYear

  // For Performance Bonus Monthly reports, only set default month if presetPeriod is not provided
  if (
    (props.type === 'Performance Bonus Monthly' ||
      props.type === 'Performance Bonus Monthly Drilldown') &&
    !props.presetPeriod
  ) {
    filteredMonth.value = [`January ${props.presetYear}`]
  }
}

watch(
  () => props.presetYear,
  (value) => {
    if (value && typeof value === 'string') {
      filteredYear.value = value

      // For Performance Bonus Monthly reports, only set default month if presetPeriod is not provided
      if (
        (props.type === 'Performance Bonus Monthly' ||
          props.type === 'Performance Bonus Monthly Drilldown') &&
        !props.presetPeriod
      ) {
        filteredMonth.value = [`January ${value}`]
      }
    }
  },
)

watch(
  () => filteredYear.value,
  (value) => {
    // Emit year change event to parent components for Performance Tracker related reports
    if (
      value &&
      (props.type === 'Performance Bonus' ||
        props.type === 'Performance Bonus Monthly' ||
        props.type === 'Performance Bonus Monthly Drilldown' ||
        props.type === 'Financed Deals' ||
        props.type === 'Accepted Not Funded Cases' ||
        props.type === 'Early Settlement Cases')
    ) {
      emit('year-change', value)
    }

    // For Performance Bonus Monthly reports, only update month if presetPeriod is not provided
    if (
      value &&
      (props.type === 'Performance Bonus Monthly' ||
        props.type === 'Performance Bonus Monthly Drilldown') &&
      !props.presetPeriod
    ) {
      filteredMonth.value = [`January ${value}`]
    }
  },
)

// Watch presetYear to ensure monthPeriods updates when it changes
watch(
  () => props.presetYear,
  () => {
    // This watch ensures the computed monthPeriods is reactive to presetYear changes
    // The computed property will automatically recalculate when presetYear changes
  },
)

const useNewVatNotesFormat = computed(() => {
  const monthValue = Array.isArray(filteredMonth.value)
    ? filteredMonth.value[0]
    : filteredMonth.value
  if (!monthValue) return false
  const parsed = parseMonthYear(monthValue)
  if (!parsed) return false
  return parsed >= VAT_NOTES_FORMAT_CUTOFF
})

const showVatNotesPartnershipHeading = computed(() => {
  if (props.type !== 'VAT Notes') return false
  if (!useNewVatNotesFormat.value) return true
  const monthValue = Array.isArray(filteredMonth.value)
    ? filteredMonth.value[0]
    : filteredMonth.value
  if (!monthValue) return true
  const parsed = parseMonthYear(monthValue)
  if (!parsed) return true
  return parsed < VAT_NOTES_PARTNERSHIP_HEADING_CUTOFF
})

const isVatNotesQuarterlyPeriod = computed(() => {
  const monthValue = Array.isArray(filteredMonth.value)
    ? filteredMonth.value[0]
    : filteredMonth.value
  if (!monthValue) return false
  const parsed = parseMonthYear(monthValue)
  if (!parsed) return false
  return parsed >= VAT_NOTES_QUARTER_START && parsed <= VAT_NOTES_QUARTER_END
})

const vatNotesPeriodLabel = computed(() => {
  const monthValue = Array.isArray(filteredMonth.value)
    ? filteredMonth.value[0]
    : filteredMonth.value
  if (!monthValue) return ''
  const parsed = parseMonthYear(monthValue)
  if (!parsed) return monthValue
  if (props.type === 'VAT Notes' && isVatNotesQuarterlyPeriod.value) {
    return getQuarterPeriodLabel(parsed)
  }
  return monthValue
})

const filteredData = computed(() => {
  if (!data.value || !Array.isArray(data.value)) return data.value

  let filtered = data.value

  filtered = filtered.filter((row: Record<string, unknown>) => {
    return !(row.is_opening_balance_only === true)
  })

  if (props.type === 'Insurance') {
    filtered = filtered.filter((row: Record<string, unknown>) => {
      const regNo = row.reg_no
      return !regNo || !String(regNo).startsWith('Total ')
    })
  }

  if (props.config?.columns) {
    const dateColumns = props.config.columns
      .filter((col: GridColumn) => col.type === 'date')
      .map((col: GridColumn) => getColumnField(col))

    filtered = filtered.map((row: Record<string, unknown>) => {
      const processedRow = { ...row }
      dateColumns.forEach((field: string) => {
        const fieldValue = processedRow[field]
        if (fieldValue && !(fieldValue instanceof Date)) {
          if (typeof fieldValue === 'string' && fieldValue.trim() !== '') {
            const dateValue = new Date(fieldValue)
            if (!isNaN(dateValue.getTime())) {
              processedRow[field] = dateValue
            }
          }
        }
      })
      return processedRow
    })
  }

  return filtered
})

const buildVatNotesExportRows = (source: DataRow[], period: string): Record<string, unknown>[] => {
  const dataMap: Record<string, number> = {}
  source.forEach((row: DataRow) => {
    const bonus = String(row.bonus || '')
    const amount = parseFloat(String(row.amount || 0)) || 0
    dataMap[bonus] = amount
  })

  const bonusMap = useNewVatNotesFormat.value
    ? {
        pfl_exc_vat: 'Finance Lease exc VAT',
        pfl_vat: 'Finance Lease VAT',
        ch_exc_vat: 'Contract Hire exc VAT',
        ch_vat: 'Contract Hire VAT',
        total_vat: 'Grand Total',
      }
    : {
        pfl_exc_vat: 'PFL Partnership Bonus exc VAT',
        pfl_vat: 'PFL Partnership Bonus (VAT)',
        ch_exc_vat: 'CH Partnership Bonus exc VAT',
        ch_vat: 'CH Partnership Bonus (VAT)',
        ic_pcp_exc_vat: 'IC & PCP Partnership Bonus exc VAT',
        ic_pcp_vat: 'IC & PCP Partnership Bonus (VAT)',
        total_exc_vat: 'Total exc VAT',
        total_vat: 'Total (VAT)',
      }

  const transformedRow: Record<string, unknown> = {
    id: 'vat-notes-row',
    period: period,
  }

  Object.entries(bonusMap).forEach(([field, bonus]) => {
    transformedRow[field] = dataMap[bonus] || 0
  })

  const totalsRow: Record<string, unknown> = {
    id: 'vat-notes-totals-row',
    is_totals_row: true,
    row_type: 'totals',
    period: 'Total',
  }

  Object.keys(bonusMap).forEach((field) => {
    totalsRow[field] = transformedRow[field]
  })

  return [transformedRow, totalsRow]
}

const emit = defineEmits(['turnover', 'row-click', 'period-change', 'year-change', 'loading-change'])

const activeFiltersCount = computed(() => {
  let count = 0
  if (filters.value?.global?.value) count++
  if (getSetting('showMonthFilter')) {
    if (isTrackerMonthlyConfig.value) {
      const selectedMonths = Array.isArray(filteredMonth.value) ? filteredMonth.value : []
      if (selectedMonths.length > 0 && selectedMonths.length < monthPeriods.value.length) {
        count++
      }
    } else {
      const monthValue = typeof filteredMonth.value === 'string' ? filteredMonth.value : ''
      if (monthValue && monthValue !== monthPeriods.value[monthPeriods.value.length - 1]) {
        count++
      }
    }
  }
  if (getSetting('showYearFilter') && filteredYear.value) count++
  return count
})

const formatDate = (date: string, format: 'short' | 'long' = 'short') => {
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''

  if (format === 'long') {
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = d.getFullYear()
    return `${day}/${month}/${year}`
  }
  return d.getDate() + '/' + (d.getMonth() + 1) + '/' + d.getFullYear()
}

const formatDateForTable = (date: string) => formatDate(date, 'long')

const formatCurrency = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 2,
})

const roundCurrency = (value: number): number => {
  return Math.round(value * 100) / 100
}

const formatCurrencyValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value) || value === 0) return '£0.00'
  if (value < 0) {
    return `(${formatCurrency.format(Math.abs(value))})`
  }
  return formatCurrency.format(value)
}

const decodeHtmlEntities = (text: string | null | undefined): string => {
  if (!text || typeof text !== 'string') {
    return text || ''
  }
  if (typeof document !== 'undefined') {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = text
    return textarea.value
  }
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
}

const renderArrayFieldValue = (
  row: Record<string, unknown>,
  fields: string[],
  columnType?: string,
): string => {
  return fields
    .map((f) => {
      if (f === 'total' && row[f]) {
        const numValue = parseFloat(String(row[f]))
        if (!isNaN(numValue)) {
          const roundedValue = columnType === 'currency' ? roundCurrency(numValue) : numValue
          return formatCurrencyValue(roundedValue)
        }
        return formatCurrencyValue(0)
      }
      const fieldVal = row[f]
      if (fieldVal !== null && fieldVal !== undefined) {
        if (columnType === 'currency') {
          const numValue = parseFloat(String(fieldVal))
          if (!isNaN(numValue)) {
            return roundCurrency(numValue).toString()
          }
        }
      }
      return decodeHtmlEntities(String(fieldVal || ''))
    })
    .join(' ')
    .trim()
}

const formatPrintedDate = (dateString: string): string => {
  if (!dateString) {
    dateString = new Date().toISOString()
  }
  let date = new Date(dateString)

  if (isNaN(date.getTime())) {
    date = new Date()
  }

  const day = date.getDate()
  const month = MONTHS[date.getMonth()]
  const year = date.getFullYear()
  return `${day} ${month} ${year}`
}

const statementPeriod = computed(() => {
  if (props.type !== 'Insurance') return null
  const monthValue = Array.isArray(filteredMonth.value)
    ? filteredMonth.value[0]
    : filteredMonth.value
  if (!monthValue) return null
  return monthValue
})

const getOpeningBalance = (reportType: string) => {
  if (props.type !== reportType || !data.value || data.value.length === 0) return 0
  return data.value[0]?.opening_balance || 0
}

const getAdvanceCommissionTotal = (): number => {
  if (props.type !== 'Commission' || !data.value || data.value.length === 0) return 0

  const totals = commissionTotals.value.total || 0
  return totals >= 0
    ? getOpeningBalance('Commission') + totals < 0
      ? 0
      : getOpeningBalance('Commission') + totals
    : 0
}

const commissionOpeningBalance = computed(() => getOpeningBalance('Commission'))
const insuranceOpeningBalance = computed(() => getOpeningBalance('Insurance'))
const advanceCommissionTotal = computed(() => getAdvanceCommissionTotal())

interface CommissionTotals {
  amountFinanced: number
  scale: number
  fixed: number
  total: number
}

interface DataRow {
  funded_amount?: string | number
  total?: string | number
  scale_commission?: string | number
  fixed_commission?: string | number
  [key: string]: unknown
}

const commissionTotals = computed((): CommissionTotals => {
  if (props.type !== 'Commission' || !filteredData.value || filteredData.value.length === 0) {
    return { amountFinanced: 0, scale: 0, fixed: 0, total: 0 }
  }

  return filteredData.value.reduce(
    (acc: CommissionTotals, row: DataRow) => {
      acc.amountFinanced += parseFloat(String(row.funded_amount || 0)) || 0
      acc.scale += parseFloat(String(row.scale_commission || 0)) || 0
      acc.fixed += parseFloat(String(row.fixed_commission || 0)) || 0
      acc.total += parseFloat(String(row.total || 0)) || 0
      return acc
    },
    { amountFinanced: 0, scale: 0, fixed: 0, total: 0 },
  )
})

const insuranceTotals = computed((): number => {
  if (props.type !== 'Insurance' || !filteredData.value || filteredData.value.length === 0) {
    return 0
  }

  const nonSubtotalData = filteredData.value.filter((row: DataRow) => {
    const regNo = row.reg_no
    const transactionType = row.transaction_type
    const isSubtotal = regNo && String(regNo).startsWith('Total ')
    const isPayment =
      (regNo && String(regNo).startsWith('PAYMENT')) ||
      (transactionType && String(transactionType).toLowerCase() === 'payment received')
    return !isSubtotal && !isPayment
  })

  return nonSubtotalData.reduce((acc: number, row: DataRow) => {
    return acc + (parseFloat(String(row.amount || 0)) || 0)
  }, 0)
})

const trackerMonthlyTotals = computed(() => {
  if (
    props.type !== 'Performance Bonus Monthly' ||
    !filteredData.value ||
    filteredData.value.length === 0
  ) {
    return { volume: 0, cases: 0 }
  }

  return filteredData.value.reduce(
    (acc: { volume: number; cases: number }, row: Record<string, unknown>) => {
      acc.volume += parseFloat(String(row.used_funded_volume_total || 0)) || 0
      acc.cases += parseFloat(String(row.used_selected_cases || 0)) || 0
      return acc
    },
    { volume: 0, cases: 0 },
  )
})

const exportTableData = computed(() => {
  if (!statementExportVisible.value) {
    return filteredData.value
  }

  if (props.type === 'VAT Notes') {
    if (!filteredData.value || filteredData.value.length === 0) {
      return []
    }

    const period = vatNotesPeriodLabel.value || ''

    return buildVatNotesExportRows(filteredData.value, period)
  }

  if (props.type === 'Performance Bonus Monthly') {
    if (!filteredData.value || filteredData.value.length === 0) {
      return filteredData.value
    }

    const totals = trackerMonthlyTotals.value
    const totalsRow: Record<string, unknown> = {
      id: 'totals-row',
      is_totals_row: true,
      row_type: 'totals',
      code: 'TOTALS',
      used_funded_volume_total: totals.volume,
      used_selected_cases: totals.cases,
    }

    return [...filteredData.value, totalsRow]
  }

  if (props.type !== 'Commission' && props.type !== 'Insurance') {
    return filteredData.value
  }

  let baseData = filteredData.value
  if (props.type === 'Insurance' && data.value && Array.isArray(data.value)) {
    baseData = data.value.filter((row: Record<string, unknown>) => {
      return !(row.is_opening_balance_only === true)
    })
  }

  if (!baseData || baseData.length === 0) {
    return baseData
  }

  if (props.type === 'Commission') {
    const totals = commissionTotals.value

    const totalsRow: Record<string, unknown> = {
      id: 'totals-row',
      is_totals_row: true,
      row_type: 'totals',
    }

    if (hasColumn('funded_amount')) {
      totalsRow.funded_amount = totals.amountFinanced
    }
    if (hasColumn('scale_commission')) {
      totalsRow.scale_commission = totals.scale
    }
    if (hasColumn('fixed_commission')) {
      totalsRow.fixed_commission = totals.fixed
    }
    totalsRow.total = totals.total
    totalsRow.vin = 'TOTALS'

    const commissionPaidRow: Record<string, unknown> = {
      id: 'commission-paid-row',
      is_totals_row: true,
      row_type: 'commission_paid',
      vin: 'COMMISSION PAID:',
      total: advanceCommissionTotal.value,
    }

    const closingBalanceRow: Record<string, unknown> = {
      id: 'closing-balance-row',
      is_totals_row: true,
      row_type: 'closing_balance',
      vin: 'CLOSING BALANCE:',
      total: commissionClosingBalance.value,
    }

    return [...baseData, totalsRow, commissionPaidRow, closingBalanceRow]
  }

  if (props.type === 'Insurance') {
    return baseData
  }

  return baseData
})

const commissionClosingBalance = computed(() => {
  if (props.type !== 'Commission') return 0

  const openingBalance = commissionOpeningBalance.value
  const commission = getAdvanceCommissionTotal()

  if (commission > 0) {
    return openingBalance + commissionTotals.value.total - commission
  }

  return openingBalance + commissionTotals.value.total + commission
})

const getExportRowClass = (data: Record<string, unknown>): string | undefined => {
  if (data.is_totals_row) {
    if (data.row_type === 'totals') return 'totals-row'
    if (data.row_type === 'commission_paid') return 'commission-paid-row'
    if (data.row_type === 'closing_balance') return 'closing-balance-row'
  }
  if (props.type === 'Insurance' && data.reg_no && String(data.reg_no).startsWith('Total ')) {
    return 'subtotal-row'
  }
  return undefined
}

const getColumnField = (column: GridColumn): string => {
  return Array.isArray(column.field) ? column.field[0] : column.field
}

const isPlatformGeneratedAgreementReference = (
  agreementRef: string | null | undefined,
): boolean => {
  if (!agreementRef || typeof agreementRef !== 'string') {
    return false
  }

  const ref = agreementRef.trim()

  if (ref.startsWith('OPEN-') && /^OPEN-[^-]+-\d{4}-\d{2}$/.test(ref)) {
    return true
  }

  if (ref.startsWith('ADV-') || ref.startsWith('ADJ-')) {
    return true
  }

  return false
}

const formatAgreementReference = (agreementRef: string | null | undefined): string => {
  if (!agreementRef) {
    return ''
  }

  if (isPlatformGeneratedAgreementReference(agreementRef)) {
    return ''
  }

  return agreementRef
}

const formatRetailerAddress = (
  retailer:
    | {
        address_line1?: string | null
        address_line2?: string | null
        address_line3?: string | null
        address_line4?: string | null
        post_code?: string | null
      }
    | null
    | undefined,
): string => {
  if (!retailer) return ''

  const addressLines: string[] = []

  if (retailer.address_line1) addressLines.push(retailer.address_line1)
  if (retailer.address_line2) addressLines.push(retailer.address_line2)
  if (retailer.address_line3) addressLines.push(retailer.address_line3)
  if (retailer.address_line4) addressLines.push(retailer.address_line4)
  if (retailer.post_code) addressLines.push(retailer.post_code)

  return addressLines.join(', ')
}

const getRetailerOrGroupCode = (): string => {
  if (props.retailer?.bank_code) {
    return String(props.retailer.bank_code).replace(/[^a-zA-Z0-9_-]/g, '_')
  }
  if (props.group?.code) {
    return String(props.group.code).replace(/[^a-zA-Z0-9_-]/g, '_')
  }
  return ''
}

const getExportFilenamePrefix = (): string => {
  const code = getRetailerOrGroupCode()
  return code ? `${code}_` : ''
}

const hasColumn = (fieldName: string): boolean => {
  if (!props.config?.columns) return false
  return visibleColumns.value.some((col: GridColumn) => {
    return getColumnField(col) === fieldName
  })
}

const clearFilter = () => {
  filters.value = filterRef.value
}

const getDateRangeFromFilters = (): { dateFrom: string | null; dateTo: string | null } => {
  if (!filters.value || !props.config?.columns) {
    return { dateFrom: null, dateTo: null }
  }

  const dateColumn = props.config.columns.find((col: GridColumn) => col.type === 'date')
  if (!dateColumn) {
    return { dateFrom: null, dateTo: null }
  }

  const dateField = getColumnField(dateColumn)
  const dateFilter = filters.value[dateField]

  if (!dateFilter) {
    return { dateFrom: null, dateTo: null }
  }

  if (dateFilter.constraints && Array.isArray(dateFilter.constraints)) {
    let dateFrom: Date | null = null
    let dateTo: Date | null = null

    dateFilter.constraints.forEach((constraint: { value: unknown; matchMode?: string }) => {
      if (!constraint.value) return

      let dateValue: Date | null = null
      if (constraint.value instanceof Date) {
        dateValue = constraint.value
      } else if (typeof constraint.value === 'string' || typeof constraint.value === 'number') {
        const parsed = new Date(constraint.value)
        if (!isNaN(parsed.getTime())) {
          dateValue = parsed
        }
      }

      if (!dateValue) return

      if (constraint.matchMode === 'dateIs' || constraint.matchMode === 'DATE_IS') {
        dateFrom = dateValue
        dateTo = dateValue
      } else if (
        constraint.matchMode === 'dateAfter' ||
        constraint.matchMode === 'DATE_AFTER' ||
        constraint.matchMode === 'dateAfterOrEqual' ||
        constraint.matchMode === 'DATE_AFTER_OR_EQUAL'
      ) {
        if (!dateFrom || dateValue < dateFrom) {
          dateFrom = dateValue
        }
      } else if (
        constraint.matchMode === 'dateBefore' ||
        constraint.matchMode === 'DATE_BEFORE' ||
        constraint.matchMode === 'dateBeforeOrEqual' ||
        constraint.matchMode === 'DATE_BEFORE_OR_EQUAL'
      ) {
        if (!dateTo || dateValue > dateTo) {
          dateTo = dateValue
        }
      } else if (
        constraint.matchMode === 'dateBetween' ||
        constraint.matchMode === 'DATE_BETWEEN'
      ) {
        if (Array.isArray(constraint.value) && constraint.value.length >= 2) {
          dateFrom =
            constraint.value[0] instanceof Date
              ? constraint.value[0]
              : new Date(constraint.value[0])
          dateTo =
            constraint.value[1] instanceof Date
              ? constraint.value[1]
              : new Date(constraint.value[1])
        }
      }
    })

    if (dateFilter.constraints.length > 1) {
      const dates = dateFilter.constraints
        .map((c: { value: unknown }) => {
          if (!c.value) return null
          if (c.value instanceof Date) return c.value
          if (typeof c.value === 'string' || typeof c.value === 'number') {
            const parsed = new Date(c.value)
            return isNaN(parsed.getTime()) ? null : parsed
          }
          return null
        })
        .filter((d: Date | null) => d !== null) as Date[]

      if (dates.length > 0) {
        const minDate = new Date(Math.min(...dates.map((d) => d.getTime())))
        const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())))

        if (dateFrom === null) {
          dateFrom = minDate
        } else {
          const currentFrom: Date = dateFrom
          if (minDate.getTime() < currentFrom.getTime()) {
            dateFrom = minDate
          }
        }

        if (dateTo === null) {
          dateTo = maxDate
        } else {
          const currentTo: Date = dateTo
          if (maxDate.getTime() > currentTo.getTime()) {
            dateTo = maxDate
          }
        }
      }
    }

    return {
      dateFrom: dateFrom ? dateFrom.toISOString().split('T')[0] : null,
      dateTo: dateTo ? dateTo.toISOString().split('T')[0] : null,
    }
  }

  if (dateFilter.value) {
    let dateValue: Date | null = null
    if (dateFilter.value instanceof Date) {
      dateValue = dateFilter.value
    } else {
      const parsed = new Date(dateFilter.value)
      if (!isNaN(parsed.getTime())) {
        dateValue = parsed
      }
    }

    if (dateValue) {
      return {
        dateFrom: dateValue.toISOString().split('T')[0],
        dateTo: dateValue.toISOString().split('T')[0],
      }
    }
  }

  return { dateFrom: null, dateTo: null }
}

const getData = async () => {
  // Clear existing data and set loading state
  data.value = null
  loading.value = true
  emit('loading-change', true)

  const postObject: Record<string, unknown> = {
    type: props.type,
    scope: props.scope,
    retailer: props.retailer,
    group: props.group,
    period: '',
    year: '',
  }

  const dateRange = getDateRangeFromFilters()
  if (dateRange.dateFrom !== null && dateRange.dateTo !== null) {
    postObject.date_from = dateRange.dateFrom
    postObject.date_to = dateRange.dateTo
  } else {
    if (getSetting('showMonthFilter')) {
      if (isTrackerMonthlyConfig.value) {
        const monthArray = Array.isArray(filteredMonth.value)
          ? filteredMonth.value
          : [filteredMonth.value]
        // Only send period if we have at least one month selected
        if (monthArray.length > 0 && monthArray[0]) {
          postObject.period = monthArray
        }
      } else {
        const monthValue = typeof filteredMonth.value === 'string' ? filteredMonth.value : ''
        if (monthValue) {
          postObject.period = monthValue
        }
      }
    }
    if (getSetting('showYearFilter')) postObject.year = filteredYear.value
  }

  if (
    (props.type === 'Performance Bonus' ||
      props.type === 'Performance Bonus Monthly' ||
      props.type === 'Performance Bonus Monthly Drilldown') &&
    props.includeProposedNotFunded !== undefined
  ) {
    postObject.include_proposed_not_funded = props.includeProposedNotFunded
  }

  axios
    .post('/api/data', postObject)
    .then(function (response) {
      data.value = props.dataKey ? response.data[props.dataKey] : response.data

      if (props.type == 'Performance Bonus') {
        emit('turnover', response.data)
      }

      currentTime.value = new Date().toLocaleString('en-GB')

      loading.value = false
      emit('loading-change', false)
    })
    .catch(() => {
      loading.value = false
      emit('loading-change', false)
    })
}

const monthPeriods = computed(() => {
  // For Performance Bonus Monthly reports, only show months for the selected year
  if (
    props.type === 'Performance Bonus Monthly' ||
    props.type === 'Performance Bonus Monthly Drilldown'
  ) {
    // Explicitly access both filteredYear and presetYear to ensure reactivity
    // Prioritize filteredYear (user selection) over presetYear (initial value)
    const yearValue = filteredYear.value || props.presetYear
    if (yearValue) {
      const selectedYear = parseInt(String(yearValue), 10)
      if (!isNaN(selectedYear) && (selectedYear === 2025 || selectedYear === 2026)) {
        // Return only months for the selected year (2025 or 2026)
        return MONTHS.map((month) => `${month} ${selectedYear}`)
      }
    }
    // Fallback: if no valid year is set, default to 2025
    return MONTHS.map((month) => `${month} 2025`)
  }

  // For other reports, show the default range (last 36 months)
  const today = new Date()
  const output = []

  for (let i = -36; i <= 0; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1)
    output.push(MONTHS[d.getMonth()] + ' ' + d.getFullYear())
  }

  return output
})

const getSetting = (setting: string) => {
  const object = props.config?.settings.find(
    (x: { name: string; value: boolean | string | unknown[] }) => x.name == setting,
  )
  return object?.value ? object.value : false
}

const getSortField = (): string => {
  const sortFields = getSetting('sortFields')
  if (Array.isArray(sortFields) && sortFields.length > 0) {
    const firstSort = sortFields[0] as { field?: string; order?: number }
    return firstSort?.field || 'id'
  }
  const sortField = getSetting('sortField')
  return sortField ? (sortField as string) : 'id'
}

const getSortOrder = (): number => {
  const sortFields = getSetting('sortFields')
  if (Array.isArray(sortFields) && sortFields.length > 0) {
    const firstSort = sortFields[0] as { field?: string; order?: number }
    return firstSort?.order || 1
  }
  return 1
}

const statementExport = ref()
const statementExportVisible = ref(false)
const exportGenerating = ref(false)

const prepareTableForPDF = () => {
  if (!statementExport.value) return null

  const table = statementExport.value.querySelector('.statement-table')
  if (!table) return null

  const actualTable = table.querySelector('table')
  if (actualTable) {
    actualTable.style.tableLayout = 'auto'
    actualTable.style.width = '100%'
  }

  const thead = table.querySelector('.p-datatable-thead')
  const tbody = table.querySelector('.p-datatable-tbody')
  if (!thead || !tbody) return null

  const headerRow = thead.querySelector('tr')
  if (!headerRow) return null

  const footer = statementExport.value.querySelector('.statement-footer')
  if (!footer) return null

  const pageHeader = statementExport.value.querySelector('.statement-header')

  const tbodyRows = Array.from(tbody.querySelectorAll('tr')) as HTMLElement[]

  if (tbodyRows.length === 0) return null

  const A4_LANDSCAPE_HEIGHT_PT = 595
  const MARGIN_TOP_PT = 20
  const MARGIN_BOTTOM_PT = 20
  const AVAILABLE_HEIGHT_PT = A4_LANDSCAPE_HEIGHT_PT - MARGIN_TOP_PT - MARGIN_BOTTOM_PT

  const getElementHeight = (element: HTMLElement | null): number => {
    if (!element) return 0
    const style = window.getComputedStyle(element)
    const height = element.offsetHeight
    const marginTop = parseFloat(style.marginTop) || 0
    const marginBottom = parseFloat(style.marginBottom) || 0
    return height + marginTop + marginBottom
  }

  const pageHeaderHeight = getElementHeight(pageHeader as HTMLElement | null)
  const retailerInfo = statementExport.value.querySelector(
    '.retailer-info-section',
  ) as HTMLElement | null
  const retailerInfoHeight = getElementHeight(retailerInfo)
  const tableHeaderHeight = getElementHeight(headerRow as HTMLElement)
  const footerHeight = getElementHeight(footer as HTMLElement)

  const pxToPt = 0.72

  const footerHeightPt = footerHeight * pxToPt

  const firstPageFixedHeight =
    pageHeaderHeight * pxToPt + retailerInfoHeight * pxToPt + tableHeaderHeight * pxToPt

  const firstPageAvailableHeight = AVAILABLE_HEIGHT_PT - firstPageFixedHeight - footerHeightPt

  const subsequentPageFixedHeight = pageHeaderHeight * pxToPt + tableHeaderHeight * pxToPt

  const subsequentPageAvailableHeight =
    AVAILABLE_HEIGHT_PT - subsequentPageFixedHeight - footerHeightPt

  const insertedHeaders: HTMLElement[] = []
  const insertedFooters: HTMLElement[] = []
  const insertedPageHeaders: HTMLElement[] = []
  const insertedTotalsRows: HTMLElement[] = []
  const tbodyParent = tbodyRows[0].parentElement
  const container = statementExport.value

  if (!tbodyParent || !container) return null

  const originalRows = [...tbodyRows]

  const totalsSection = statementExport.value.querySelector('.totals-section')
  if (totalsSection) {
    ;(totalsSection as HTMLElement).style.display = 'none'
  }

  const getColumnIndex = (fieldName: string): number => {
    let index = 0
    for (const col of visibleColumns.value) {
      if (Array.isArray(col.field)) {
        if (col.field.includes(fieldName)) {
          return index
        }
        index++
      } else {
        if (getColumnField(col) === fieldName) {
          return index
        }
        index++
      }
    }
    return -1
  }

  const vinColumnIndex = getColumnIndex('vin')

  const hasTotalsInData = Array.from(tbodyRows).some((row) => {
    const cells = row.querySelectorAll('td')
    if (props.type === 'Commission' && vinColumnIndex >= 0 && cells.length > vinColumnIndex) {
      const vinCellText = cells[vinColumnIndex]?.textContent?.trim()
      return (
        vinCellText === 'TOTALS' ||
        vinCellText === 'COMMISSION PAID:' ||
        vinCellText === 'CLOSING BALANCE:'
      )
    } else if (props.type === 'Insurance') {
      for (let i = 0; i < cells.length; i++) {
        const cellText = cells[i]?.textContent?.trim()
        if (
          cellText === 'TOTALS' ||
          cellText === 'Total' ||
          cellText === 'Totals To Be Collected'
        ) {
          return true
        }
      }
    }
    return false
  })

  if (
    (props.type === 'Commission' || props.type === 'Insurance') &&
    filteredData.value &&
    filteredData.value.length > 0 &&
    !hasTotalsInData
  ) {
    const headerCells = Array.from(headerRow.querySelectorAll('th')) as HTMLElement[]
    const columnCount = headerCells.length

    if (props.type === 'Commission') {
      const fundedAmountIndex = getColumnIndex('funded_amount')
      const scaleIndex = getColumnIndex('scale_commission')
      const fixedIndex = getColumnIndex('fixed_commission')
      const totalIndex = getColumnIndex('total')

      const totals = commissionTotals.value

      const totalsRow = document.createElement('tr')
      totalsRow.style.fontWeight = 'bold'
      totalsRow.style.backgroundColor = '#f3f4f6'
      for (let i = 0; i < columnCount; i++) {
        const cell = document.createElement('td')
        cell.style.padding = '8px 4px'
        cell.style.border = '1px solid #d1d5db'
        if (i === vinColumnIndex) {
          cell.textContent = 'TOTALS'
          cell.style.color = '#3b82f6'
        } else if (i === fundedAmountIndex && hasColumn('funded_amount')) {
          cell.textContent = formatCurrencyValue(totals.amountFinanced)
        } else if (i === scaleIndex && hasColumn('scale_commission')) {
          cell.textContent = formatCurrencyValue(totals.scale)
        } else if (i === fixedIndex && hasColumn('fixed_commission')) {
          cell.textContent = formatCurrencyValue(totals.fixed)
        } else if (i === totalIndex) {
          cell.textContent = formatCurrencyValue(totals.total)
        }
        totalsRow.appendChild(cell)
      }
      tbodyParent.appendChild(totalsRow)
      insertedTotalsRows.push(totalsRow)

      const commissionPaidRow = document.createElement('tr')
      commissionPaidRow.style.fontWeight = 'bold'
      for (let i = 0; i < columnCount; i++) {
        const cell = document.createElement('td')
        cell.style.padding = '8px 4px'
        cell.style.border = '1px solid #d1d5db'
        if (i === vinColumnIndex) {
          cell.textContent = 'COMMISSION PAID:'
          cell.style.color = '#3b82f6'
        } else if (i === totalIndex) {
          cell.textContent = formatCurrencyValue(advanceCommissionTotal.value)
        }
        commissionPaidRow.appendChild(cell)
      }
      tbodyParent.appendChild(commissionPaidRow)
      insertedTotalsRows.push(commissionPaidRow)

      const closingBalanceRow = document.createElement('tr')
      closingBalanceRow.style.fontWeight = 'bold'
      closingBalanceRow.style.color = '#3b82f6'
      for (let i = 0; i < columnCount; i++) {
        const cell = document.createElement('td')
        cell.style.padding = '8px 4px'
        cell.style.border = '1px solid #d1d5db'
        if (i === vinColumnIndex) {
          cell.textContent = 'CLOSING BALANCE:'
          cell.style.color = '#3b82f6'
        } else if (i === totalIndex) {
          cell.textContent = formatCurrencyValue(commissionClosingBalance.value)
          cell.style.color = '#3b82f6'
        }
        closingBalanceRow.appendChild(cell)
      }
      tbodyParent.appendChild(closingBalanceRow)
      insertedTotalsRows.push(closingBalanceRow)
    } else if (props.type === 'Insurance') {
      const regNoColumnIndex = getColumnIndex('reg_no')
      const amountColumnIndex = getColumnIndex('amount')
      const totals = insuranceTotals.value

      const totalsRow = document.createElement('tr')
      totalsRow.style.fontWeight = 'bold'
      totalsRow.style.backgroundColor = '#f3f4f6'
      for (let i = 0; i < columnCount; i++) {
        const cell = document.createElement('td')
        cell.style.padding = '8px 4px'
        cell.style.border = '1px solid #d1d5db'
        if (i === regNoColumnIndex && regNoColumnIndex >= 0) {
          cell.textContent = 'Totals To Be Collected'
          cell.style.color = '#3b82f6'
        } else if (i === amountColumnIndex && amountColumnIndex >= 0) {
          cell.textContent = formatCurrencyValue(totals)
        }
        totalsRow.appendChild(cell)
      }
      tbodyParent.appendChild(totalsRow)
      insertedTotalsRows.push(totalsRow)
    }
  }

  const pageBreaks: number[] = []
  let currentPageHeight = 0
  let availableHeight = firstPageAvailableHeight

  for (let i = 0; i < originalRows.length; i++) {
    const row = originalRows[i]
    const rowHeight = row.getBoundingClientRect().height * pxToPt

    if (currentPageHeight + rowHeight + footerHeightPt > availableHeight && i > 0) {
      pageBreaks.push(i)
      currentPageHeight = rowHeight
      availableHeight = subsequentPageAvailableHeight
    } else {
      currentPageHeight += rowHeight + 3
    }
  }

  const totalPages = pageBreaks.length + 1

  if (!currentTime.value) {
    currentTime.value = new Date().toISOString()
  }

  const pageNumberEl = footer.querySelector('.page-number')
  const totalPagesEl = footer.querySelector('.total-pages')
  if (pageNumberEl) pageNumberEl.textContent = totalPages.toString()
  if (totalPagesEl) totalPagesEl.textContent = totalPages.toString()

  const headerRowClone = headerRow.cloneNode(true) as HTMLElement
  const footerClone = footer.cloneNode(true) as HTMLElement
  const pageHeaderClone = pageHeader ? (pageHeader.cloneNode(true) as HTMLElement) : null

  const columnCount = thead.querySelectorAll('th').length

  // Calculate table's left offset relative to container for header positioning
  const tableRect = table.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()
  const tableLeftOffset = tableRect.left - containerRect.left

  for (let i = pageBreaks.length + 1; i >= 0; i--) {
    const rowIndex = pageBreaks[i]
    const pageNumber = i + 1

    if (rowIndex >= originalRows.length) continue

    const targetRow = originalRows[rowIndex]
    if (!targetRow || !targetRow.parentElement) continue
    const previousPageLastRow = originalRows[rowIndex - 1]
    let insertionPoint: HTMLElement = targetRow

    const newHeaderRow = headerRowClone.cloneNode(true) as HTMLElement
    newHeaderRow.classList.add('pdf-repeated-header')
    newHeaderRow.style.display = 'table-row'
    newHeaderRow.style.pageBreakAfter = 'avoid'
    newHeaderRow.style.pageBreakInside = 'avoid'
    tbodyParent.insertBefore(newHeaderRow, insertionPoint)
    insertedHeaders.push(newHeaderRow)
    insertionPoint = newHeaderRow

    if (pageHeaderClone) {
      const newPageHeader = pageHeaderClone.cloneNode(true) as HTMLElement
      newPageHeader.classList.add('pdf-repeated-page-header')
      newPageHeader.style.display = 'flex'
      newPageHeader.style.width = '100%'
      newPageHeader.style.minWidth = '100%'
      newPageHeader.style.maxWidth = '100%'
      newPageHeader.style.boxSizing = 'border-box'
      newPageHeader.style.marginLeft = '0'
      newPageHeader.style.marginRight = '0'

      const pageHeaderDiv = document.createElement('div')
      pageHeaderDiv.classList.add('pdf-repeated-page-header-div')
      pageHeaderDiv.style.display = 'block'
      pageHeaderDiv.style.width = '100%'
      if (tableLeftOffset > 0) {
        pageHeaderDiv.style.marginLeft = `-${tableLeftOffset}px`
        pageHeaderDiv.style.width = `calc(100% + ${tableLeftOffset}px)`
      } else {
        pageHeaderDiv.style.marginLeft = '0'
      }
      pageHeaderDiv.style.marginRight = '0'
      pageHeaderDiv.style.padding = '0'
      pageHeaderDiv.style.pageBreakAfter = 'avoid'
      pageHeaderDiv.style.pageBreakInside = 'avoid'
      pageHeaderDiv.appendChild(newPageHeader)

      const pageHeaderRow = document.createElement('tr')
      pageHeaderRow.classList.add('pdf-repeated-page-header-row')
      const pageHeaderCell = document.createElement('td')
      pageHeaderCell.colSpan = columnCount
      pageHeaderCell.style.padding = '0'
      pageHeaderCell.style.margin = '0'
      pageHeaderCell.style.border = 'none'
      pageHeaderCell.style.height = '0'
      pageHeaderCell.style.lineHeight = '0'
      pageHeaderCell.style.fontSize = '0'
      pageHeaderCell.style.overflow = 'visible'
      pageHeaderCell.appendChild(pageHeaderDiv)
      pageHeaderRow.appendChild(pageHeaderCell)
      tbodyParent.insertBefore(pageHeaderRow, insertionPoint)
      insertedPageHeaders.push(pageHeaderRow)
      insertionPoint = pageHeaderRow
    }

    if (previousPageLastRow && previousPageLastRow.parentElement) {
      const footerRow = document.createElement('tr')
      footerRow.classList.add('pdf-repeated-footer-row')
      const footerCell = document.createElement('td')
      footerCell.colSpan = columnCount
      footerCell.style.padding = '0'
      footerCell.style.border = 'none'

      const newFooter = footerClone.cloneNode(true) as HTMLElement
      newFooter.classList.add('pdf-repeated-footer')
      newFooter.style.marginTop = '10px'
      newFooter.style.padding = '15px 10px'
      newFooter.style.borderTop = '2px solid #333'
      newFooter.style.fontSize = '11px'

      const pageNumberEl = newFooter.querySelector('.page-number')
      const totalPagesEl = newFooter.querySelector('.total-pages')
      if (pageNumberEl) pageNumberEl.textContent = pageNumber.toString()
      if (totalPagesEl) totalPagesEl.textContent = totalPages.toString()

      const footerInfo = newFooter.querySelector('.footer-info')
      if (footerInfo) {
        const printedSpan = footerInfo.querySelector('span:first-child')
        if (printedSpan && currentTime.value) {
          printedSpan.textContent = `Printed: ${formatPrintedDate(currentTime.value)}`
        }
      }

      footerCell.appendChild(newFooter)
      footerRow.appendChild(footerCell)

      tbodyParent.insertBefore(footerRow, insertionPoint)
      insertedFooters.push(footerRow)
    }
  }

  return () => {
    insertedHeaders.forEach((header) => {
      if (header.parentElement) {
        header.remove()
      }
    })
    insertedFooters.forEach((footer) => {
      if (footer.parentElement) {
        footer.remove()
      }
    })
    insertedPageHeaders.forEach((header) => {
      if (header.parentElement) {
        header.remove()
      }
    })
    insertedTotalsRows.forEach((row) => {
      if (row.parentElement) {
        row.remove()
      }
    })
    const totalsSection = statementExport.value?.querySelector('.totals-section')
    if (totalsSection) {
      ;(totalsSection as HTMLElement).style.display = ''
    }
  }
}

const convertCSSVariablesToColors = () => {
  if (!statementExport.value) return null

  const elements = statementExport.value.querySelectorAll('*')
  const originalStyles: Map<HTMLElement, string> = new Map()
  elements.forEach((el: Element) => {
    const htmlEl = el as HTMLElement
    if (!(htmlEl instanceof HTMLElement)) return

    const computedStyle = window.getComputedStyle(htmlEl)
    const style = htmlEl.style

    const properties = [
      'background-color',
      'color',
      'border-color',
      'border-top-color',
      'border-bottom-color',
      'border-left-color',
      'border-right-color',
    ]

    properties.forEach((prop) => {
      const computedValue = computedStyle.getPropertyValue(prop)

      if (
        !computedValue ||
        computedValue === 'transparent' ||
        computedValue === 'rgba(0, 0, 0, 0)'
      ) {
        return
      }

      if (
        computedValue.includes('oklch') ||
        (!computedValue.startsWith('rgb') &&
          !computedValue.startsWith('#') &&
          computedValue !== 'transparent')
      ) {
        try {
          const tempEl = document.createElement('div')
          tempEl.style.position = 'fixed'
          tempEl.style.left = '-9999px'
          tempEl.style.top = '-9999px'
          tempEl.style.width = '1px'
          tempEl.style.height = '1px'
          tempEl.style.setProperty(prop, computedValue, 'important')
          document.body.appendChild(tempEl)

          void tempEl.offsetHeight
          void tempEl.offsetWidth

          const rgbValue = window.getComputedStyle(tempEl).getPropertyValue(prop)
          document.body.removeChild(tempEl)

          if (rgbValue && rgbValue.includes('oklch')) {
            const canvas = document.createElement('canvas')
            canvas.width = 1
            canvas.height = 1
            const ctx = canvas.getContext('2d')
            if (ctx) {
              ctx.fillStyle = computedValue
              ctx.fillRect(0, 0, 1, 1)
              const imageData = ctx.getImageData(0, 0, 1, 1)
              const [r, g, b, a] = imageData.data
              const finalRgb =
                a === 255 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${a / 255})`

              if (!originalStyles.has(htmlEl)) {
                originalStyles.set(htmlEl, style.cssText)
              }
              style.setProperty(prop, finalRgb, 'important')
            }
          } else if (rgbValue && rgbValue.startsWith('rgb') && rgbValue !== 'rgba(0, 0, 0, 0)') {
            if (!originalStyles.has(htmlEl)) {
              originalStyles.set(htmlEl, style.cssText)
            }

            style.setProperty(prop, rgbValue, 'important')
          }
        } catch (e) {
          console.warn('Failed to convert color:', prop, computedValue, e)
        }
      }
    })
  })

  void statementExport.value.offsetHeight

  return () => {
    originalStyles.forEach((originalStyle, el) => {
      el.style.cssText = originalStyle
    })
  }
}

const exportPDF = () => {
  exportGenerating.value = true

  if (!statementExport.value) {
    exportGenerating.value = false
    return
  }

  currentTime.value = new Date().toISOString()

  const cleanup = prepareTableForPDF()
  let colorCleanup: (() => void) | null = null

  setTimeout(() => {
    colorCleanup = convertCSSVariablesToColors()

    setTimeout(() => {
      if (!statementExport.value) {
        if (cleanup) cleanup()
        if (colorCleanup) colorCleanup()
        exportGenerating.value = false
        return
      }

      const element = statementExport.value
      const tableElement = element.querySelector('.statement-table') as HTMLElement | null

      // Fit wide tables (like Commission) into the PDF page width to avoid right-side clipping.
      // html2pdf/jsPDF uses A4 landscape in points; convert the usable width to pixels.
      const A4_LANDSCAPE_WIDTH_PT = 842
      const LEFT_MARGIN_PT = 10
      const RIGHT_MARGIN_PT = 10
      const PT_TO_PX = 96 / 72
      const usablePageWidthPx =
        (A4_LANDSCAPE_WIDTH_PT - LEFT_MARGIN_PT - RIGHT_MARGIN_PT) * PT_TO_PX

      const zoomTarget = tableElement ?? element
      const originalZoom = zoomTarget.style.zoom
      const originalWidth = zoomTarget.style.width
      const contentWidthPx = zoomTarget.scrollWidth
      const fitZoom = contentWidthPx > 0 ? Math.min(1, usablePageWidthPx / contentWidthPx) : 1

      // Repeated headers/footers are injected into the table for later pages.
      // If we scale the table, they get squished; apply inverse zoom to keep them readable.
      const repeatedHeaderFooterEls = Array.from(
        element.querySelectorAll(
          '.pdf-repeated-page-header, .pdf-repeated-page-header-div, .pdf-repeated-footer',
        ),
      ) as HTMLElement[]
      const repeatedOriginalZooms = repeatedHeaderFooterEls.map((el) => el.style.zoom)

      if (fitZoom < 1) {
        zoomTarget.style.zoom = `${fitZoom}`
        // Lock the layout width before zooming so column widths stay consistent.
        zoomTarget.style.width = `${contentWidthPx}px`

        const inverseZoom = `${1 / fitZoom}`
        repeatedHeaderFooterEls.forEach((el) => {
          el.style.zoom = inverseZoom
        })

        void zoomTarget.offsetHeight
      }

      const elementWidth = element.offsetWidth
      const elementHeight = element.offsetHeight

      const maxCanvasSize = 14000
      const baseScale = 2

      const widthScale =
        elementWidth > 0 ? Math.min(baseScale, maxCanvasSize / elementWidth) : baseScale
      const heightScale =
        elementHeight > 0 ? Math.min(baseScale, maxCanvasSize / elementHeight) : baseScale
      const calculatedScale = Math.min(widthScale, heightScale, baseScale)

      const finalScale = Math.max(1, Math.min(calculatedScale, 2))

      html2pdf()
        .set({
          margin: [10, 10, 20, 10],
          filename: `${getExportFilenamePrefix()}${props.type}_statement_${new Date().toISOString().split('T')[0]}.pdf`,
          html2canvas: {
            dpi: 192,
            letterRendering: true,
            scale: finalScale,
            useCORS: true,
            logging: false,
            windowWidth: elementWidth,
            windowHeight: elementHeight,
            allowTaint: false,
          },
          image: { type: 'jpeg', quality: 0.95 },
          jsPDF: {
            unit: 'pt',
            format: 'a4',
            orientation: 'landscape',
          },
          // @ts-expect-error - pagebreak option exists in html2pdf but types may be outdated
          pagebreak: {
            mode: ['css', 'legacy'],
            after: '.statement-footer',
            avoid: [
              // '.statement-header',
              // '.totals-section',
              // '.statement-footer',
              // '.retailer-info-section',
              // '.statement-table',
            ],
          },
        })
        .from(statementExport.value)
        .save()
        .then(() => {
          zoomTarget.style.zoom = originalZoom
          zoomTarget.style.width = originalWidth
          repeatedHeaderFooterEls.forEach((el, i) => {
            el.style.zoom = repeatedOriginalZooms[i]
          })
          if (cleanup) cleanup()
          if (colorCleanup) colorCleanup()
          statementExportVisible.value = false
          exportGenerating.value = false
        })
        .catch((error) => {
          console.error('PDF export error:', error)
          zoomTarget.style.zoom = originalZoom
          zoomTarget.style.width = originalWidth
          repeatedHeaderFooterEls.forEach((el, i) => {
            el.style.zoom = repeatedOriginalZooms[i]
          })
          if (cleanup) cleanup()
          if (colorCleanup) colorCleanup()
          exportGenerating.value = false

          alert(
            'PDF export failed. The document may be too large. Please try exporting a smaller dataset or contact support.',
          )
        })
    }, 100)
  }, 100)
}

onMounted(async () => {
  const globals: Array<string> = []
  filterRef.value = { global: { value: null, matchMode: FilterMatchMode.CONTAINS } }

  props.config.columns?.forEach((col: GridColumn) => {
    globals.push(col.field)
    if (col.filter) {
      filterRef.value[col.field] = {
        operator: col.filter.operator,
        constraints: col.filter.constraints,
      }
    }
  })

  isInitializing.value = true
  filters.value = filterRef.value
  globalFilters.value = globals
  initializeColumnVisibility()

  await nextTick()

  isInitializing.value = false
  hasInitialLoad.value = true
  getData()
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
  }
})

watch(
  [
    () => props.type,
    () => props.scope,
    () => props.retailer,
    () => props.group,
    () => filteredMonth,
    () => filteredYear,
    () => props.includeProposedNotFunded,
    () => props.presetPeriod,
  ],
  () => {
    if (hasInitialLoad.value && !isInitializing.value) {
      getData()
      // Reset mobile pagination when filters change
      mobileCurrentPage.value = 1
      expandedCardIds.value = []
    }
  },
  { deep: true },
)

watch(
  () => filteredMonth.value,
  (value) => {
    if (isTrackerMonthlyConfig.value) {
      emit('period-change', value)
    } else if (props.type === 'Insurance' || props.type === 'VAT Notes') {
      emit(
        'period-change',
        typeof value === 'string' ? value : Array.isArray(value) ? value[0] : '',
      )
    }
  },
  { immediate: true },
)

const serverDateRangeSignature = computed(() => {
  const { dateFrom, dateTo } = getDateRangeFromFilters()
  return `${dateFrom ?? ''}|${dateTo ?? ''}`
})

watch(serverDateRangeSignature, (newSignature, oldSignature) => {
  if (newSignature === oldSignature) {
    return
  }

  if (filters.value && hasInitialLoad.value && !isInitializing.value) {
    getData()
    // Reset mobile pagination when the server-side date range changes
    mobileCurrentPage.value = 1
    expandedCardIds.value = []
  }
})

const datatable = ref()

const getRowClass = () => {
  if (props.type === 'Performance Bonus' || props.type === 'Performance Bonus Monthly') {
    return 'cursor-pointer'
  }
  return ''
}

const handleRowClick = (event: { data: Record<string, unknown> }) => {
  if (props.type === 'Performance Bonus' || props.type === 'Performance Bonus Monthly') {
    emit('row-click', event.data)
  }
}

const compareValues = (aValue: unknown, bValue: unknown, sortOrder: number): number => {
  if (aValue === null || aValue === undefined) {
    return sortOrder > 0 ? 1 : -1
  }
  if (bValue === null || bValue === undefined) {
    return sortOrder > 0 ? -1 : 1
  }

  if (aValue instanceof Date && bValue instanceof Date) {
    const diff = aValue.getTime() - bValue.getTime()
    return sortOrder > 0 ? diff : -diff
  }

  if (typeof aValue === 'number' && typeof bValue === 'number') {
    const diff = aValue - bValue
    return sortOrder > 0 ? diff : -diff
  }

  if (typeof aValue === 'string' && typeof bValue === 'string') {
    const diff = aValue.localeCompare(bValue)
    return sortOrder > 0 ? diff : -diff
  }

  const aStr = String(aValue)
  const bStr = String(bValue)
  const diff = aStr.localeCompare(bStr)
  return sortOrder > 0 ? diff : -diff
}

const sortDataForExport = (data: Record<string, unknown>[]): Record<string, unknown>[] => {
  if (!data || data.length === 0) return data

  const sortFields = getSetting('sortFields')
  const sorted = [...data]

  if (Array.isArray(sortFields) && sortFields.length > 0) {
    sorted.sort((a, b) => {
      for (const sortConfig of sortFields) {
        const config = sortConfig as { field: string; order?: number }
        const field = config.field
        const order = config.order || 1
        const aValue = a[field]
        const bValue = b[field]
        const comparison = compareValues(aValue, bValue, order)
        if (comparison !== 0) {
          return comparison
        }
      }
      return 0
    })
  } else {
    const sortField = getSetting('sortField') ? (getSetting('sortField') as string) : 'id'
    const sortOrder = 1

    sorted.sort((a, b) => {
      const aValue = a[sortField]
      const bValue = b[sortField]
      return compareValues(aValue, bValue, sortOrder)
    })
  }

  return sorted
}

const getColumnIndexInHeaders = (fieldName: string, startIndex: number = 0): number => {
  let currentIndex = startIndex
  for (const col of visibleColumns.value) {
    if (isTotalDebitCreditField(col)) {
      if (fieldName === 'total') {
        return currentIndex
      }
      currentIndex += 2
    } else {
      const field = getColumnField(col)
      if (field === fieldName) {
        return currentIndex
      }
      currentIndex++
    }
  }
  return -1
}

const createEmptyRow = (numColumns: number): (string | number)[] => {
  return new Array(numColumns).fill('')
}

const getDateRangeForExport = () => {
  if (!filters.value || !props.config?.columns) return { dateFrom: null, dateTo: null }

  const dateColumn = props.config.columns.find((col: GridColumn) => col.type === 'date')
  if (!dateColumn) return { dateFrom: null, dateTo: null }

  const dateField = getColumnField(dateColumn)
  const dateFilter = filters.value[dateField]
  if (!dateFilter) return { dateFrom: null, dateTo: null }

  const extractDate = (val: unknown): string | null => {
    if (!val) return null
    if (val instanceof Date) return val.toISOString().split('T')[0]
    const parsed = new Date(val as string | number)
    return isNaN(parsed.getTime()) ? null : parsed.toISOString().split('T')[0]
  }

  if (dateFilter.constraints && Array.isArray(dateFilter.constraints)) {
    const dates = dateFilter.constraints
      .map((c: { value: unknown }) => extractDate(c.value))
      .filter((d: string | null) => d !== null) as string[]

    if (dates.length === 0) return { dateFrom: null, dateTo: null }

    return {
      dateFrom: dates.reduce((min, d) => (!min || d < min ? d : min), dates[0]),
      dateTo: dates.reduce((max, d) => (!max || d > max ? d : max), dates[0]),
    }
  }

  if (dateFilter.value) {
    const single = extractDate(dateFilter.value)
    return { dateFrom: single, dateTo: single }
  }

  return { dateFrom: null, dateTo: null }
}

const sheetLayouts: Record<string, { field: string; header: string }[]> = {
  FinancedDeals: FinancedDealsConfiguration.columns.map((c: GridColumn) => ({
    field: Array.isArray(c.field) ? String(c.field[0]) : String(c.field),
    header: c.header || '',
  })),
  AcceptedNotFunded: AcceptedNotFundedConfiguration.columns.map((c: GridColumn) => ({
    field: Array.isArray(c.field) ? String(c.field[0]) : String(c.field),
    header: c.header || '',
  })),
  MonthlyDrilldown: TrackerDrilldownConfiguration.columns.map((c: GridColumn) => ({
    field: Array.isArray(c.field) ? String(c.field[0]) : String(c.field),
    header: c.header || '',
  })),
}

const buildWorksheetFromRecords = (records: Record<string, unknown>[], sheetName: string) => {
  if (!records || records.length === 0) return null

  const layout = sheetLayouts[sheetName]

  // Always prepend Retailer Code as column A for the extra worksheets
  const baseFields: { field: string; header: string }[] = [
    { field: 'dealer_code', header: 'Retailer Code' },
  ]
  if (layout) {
    baseFields.push(...layout)
  }

  const headers = layout ? baseFields.map((c) => c.header) : Object.keys(records[0])
  const fields = layout ? baseFields.map((c) => c.field) : headers

  // Sort by Retailer Code, then Paid Date (funded_date), falling back to Proposed Date
  const sortedRecords = [...records].sort((a, b) => {
    const codeA = String(a.dealer_code ?? '').localeCompare(String(b.dealer_code ?? ''))
    if (codeA !== 0) return codeA
    const dateField = sheetName === 'AcceptedNotFunded' ? 'proposal_date' : 'funded_date'
    const parseDate = (val: unknown) => {
      const d = val ? new Date(String(val)) : null
      return d && !isNaN(d.getTime()) ? d.getTime() : null
    }
    const aDate = parseDate(a[dateField])
    const bDate = parseDate(b[dateField])
    return aDate && bDate ? aDate - bDate : 0
  })

  const rows = sortedRecords.map((row) => fields.map((f) => row[f] ?? ''))
  const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows])

  // Apply date formatting for known date columns
  const dateColumnsBySheet: Record<string, string[]> = {
    FinancedDeals: ['proposal_date', 'funded_date'],
    AcceptedNotFunded: ['proposal_date'],
    MonthlyDrilldown: ['proposal_date', 'funded_date'],
  }
  const dateFields = dateColumnsBySheet[sheetName] || []
  const currencyColumnsBySheet: Record<string, string[]> = {
    FinancedDeals: [
      'financed_amount',
      'commission_amount_without_tax',
      'pays_value',
      'b2b_pays_value',
    ],
    AcceptedNotFunded: [
      'financed_amount',
      'commission_amount_without_tax',
      'pays_value',
      'b2b_pays_value',
    ],
    MonthlyDrilldown: ['financed_amount'],
  }
  const currencyFields = currencyColumnsBySheet[sheetName] || []

  if (dateFields.length && sheet['!ref']) {
    const range = XLSX.utils.decode_range(sheet['!ref'])
    // build index lookup for date fields
    const fieldIndex: Record<string, number> = {}
    fields.forEach((f, idx) => {
      fieldIndex[f] = idx
    })
    for (let R = 1; R <= range.e.r; R++) {
      for (const df of dateFields) {
        const C = fieldIndex[df]
        if (C === undefined) continue
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
        const cell = sheet[cellAddress]
        if (!cell || cell.v === undefined || cell.v === null || cell.v === '') continue
        const parsed = new Date(cell.v)
        if (!isNaN(parsed.getTime())) {
          cell.v = parsed
          cell.t = 'd'
          cell.z = 'dd/mm/yyyy'
        }
      }
    }
  }

  // Apply currency formatting
  if (currencyFields.length && sheet['!ref']) {
    const range = XLSX.utils.decode_range(sheet['!ref'])
    const fieldIndex: Record<string, number> = {}
    fields.forEach((f, idx) => {
      fieldIndex[f] = idx
    })
    for (let R = 1; R <= range.e.r; R++) {
      for (const cf of currencyFields) {
        const C = fieldIndex[cf]
        if (C === undefined) continue
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
        const cell = sheet[cellAddress]
        if (!cell || cell.v === undefined || cell.v === null || cell.v === '') continue
        const num = Number(cell.v)
        if (!isNaN(num)) {
          cell.v = num
          cell.t = 'n'
          cell.z = '£#,##0.00'
        }
      }
    }
  }

  return { sheet, sheetName }
}

const exportCSV = async () => {
  exportGenerating.value = true
  let exportData = sortDataForExport(filteredData.value)

  if (props.type === 'VAT Notes') {
    if (!exportData || exportData.length === 0) {
      exportGenerating.value = false
      return
    }

    const period = vatNotesPeriodLabel.value || ''
    exportData = buildVatNotesExportRows(exportData, period)
  }

  if (!exportData || exportData.length === 0) {
    exportGenerating.value = false
    return
  }

  const headers: string[] = []

  if (isGroupCommission.value) {
    headers.push('Retailer')
  }

  const columnsToUse = props.type === 'VAT Notes' ? vatNotesColumns.value : visibleColumns.value

  if (props.type === 'Commission') {
    columnsToUse.forEach((column: GridColumn) => {
      if (isTotalDebitCreditField(column)) {
        headers.push('Total')
        headers.push('Credit/ Debit')
      } else {
        headers.push(column.header)
      }
    })
  } else {
    columnsToUse.forEach((column: GridColumn) => {
      headers.push(column.header)
    })
  }

  const rows: (string | number)[][] = exportData.map((row: Record<string, unknown>) => {
    const rowData: (string | number)[] = []

    if (isGroupCommission.value) {
      const retailerCode = row['sub_dealer_code']
      rowData.push(
        retailerCode !== null && retailerCode !== undefined
          ? decodeHtmlEntities(String(retailerCode))
          : '',
      )
    }

    const columnsToUse = props.type === 'VAT Notes' ? vatNotesColumns.value : visibleColumns.value

    columnsToUse.forEach((column: GridColumn) => {
      let value: string | number

      if (Array.isArray(column.field)) {
        if (props.type === 'Commission' && isTotalDebitCreditField(column)) {
          const totalValue = row['total']
          const debitCreditValue = row['debit_credit']

          if (totalValue !== null && totalValue !== undefined) {
            const numValue = parseFloat(String(totalValue))
            if (!isNaN(numValue)) {
              value = roundCurrency(numValue)
            } else {
              value = String(totalValue)
            }
          } else {
            value = ''
          }
          rowData.push(value)

          value =
            debitCreditValue !== null && debitCreditValue !== undefined
              ? decodeHtmlEntities(String(debitCreditValue))
              : ''
          rowData.push(value)
        } else {
          value = renderArrayFieldValue(row, column.field, column.type)
          rowData.push(value !== null && value !== undefined ? value : '')
        }
      } else {
        const fieldValue = row[column.field]
        if (fieldValue !== null && fieldValue !== undefined) {
          if (column.type === 'currency') {
            const numValue = parseFloat(String(fieldValue))
            if (!isNaN(numValue)) {
              value = roundCurrency(numValue)
            } else {
              value = String(fieldValue)
            }
          } else {
            if (typeof fieldValue === 'number') {
              value = fieldValue
            } else {
              value = String(fieldValue)
            }
          }
        } else {
          value = ''
        }

        if (column.type === 'date' && value) {
          value = formatDateForTable(String(value))
        } else if (column.type === 'currency' && value !== null && value !== undefined) {
          const numValue = parseFloat(String(value))
          if (!isNaN(numValue)) {
            value = roundCurrency(numValue)
          } else {
            value = 0
          }
        } else if (column.type === 'percent' && value !== null && value !== undefined) {
          const numValue = parseFloat(String(value))
          if (!isNaN(numValue)) {
            value = numValue / 100
          } else {
            value = 0
          }
        } else if (
          column.field === 'agreement_reference' &&
          value !== null &&
          value !== undefined
        ) {
          value = formatAgreementReference(String(value))
        } else if (typeof value === 'string' && value !== '') {
          value = decodeHtmlEntities(value)
        }

        rowData.push(value !== null && value !== undefined ? value : '')
      }
    })

    return rowData
  })

  const worksheetData = [headers, ...rows]

  if (props.type === 'Insurance' && exportData.length > 0) {
    const totals = insuranceTotals.value
    const numColumns = headers.length

    const regNoColumnIndex = getColumnIndexInHeaders('reg_no', 0)
    const amountColumnIndex = getColumnIndexInHeaders('amount', 0)

    const totalsRow = createEmptyRow(numColumns)
    if (regNoColumnIndex >= 0) {
      totalsRow[regNoColumnIndex] = 'Total'
    }
    if (amountColumnIndex >= 0) {
      totalsRow[amountColumnIndex] = roundCurrency(totals)
    }
    worksheetData.push(totalsRow)
  }

  if (props.type === 'Commission' && exportData.length > 0) {
    const totals = commissionTotals.value
    const numColumns = headers.length

    const startIndex = isGroupCommission.value ? 1 : 0
    const vinColumnIndex = getColumnIndexInHeaders('vin', startIndex)
    const fundedAmountIndex = getColumnIndexInHeaders('funded_amount', startIndex)
    const scaleIndex = getColumnIndexInHeaders('scale_commission', startIndex)
    const fixedIndex = getColumnIndexInHeaders('fixed_commission', startIndex)
    const totalIndex = getColumnIndexInHeaders('total', startIndex)

    const totalsRow = createEmptyRow(numColumns)
    if (fundedAmountIndex >= 0 || scaleIndex >= 0 || fixedIndex >= 0 || totalIndex >= 0) {
      if (vinColumnIndex >= 0) {
        totalsRow[vinColumnIndex] = 'TOTALS'
      }
      if (fundedAmountIndex >= 0 && hasColumn('funded_amount')) {
        totalsRow[fundedAmountIndex] = roundCurrency(totals.amountFinanced)
      }
      if (scaleIndex >= 0 && hasColumn('scale_commission')) {
        totalsRow[scaleIndex] = roundCurrency(totals.scale)
      }
      if (fixedIndex >= 0 && hasColumn('fixed_commission')) {
        totalsRow[fixedIndex] = roundCurrency(totals.fixed)
      }
      if (totalIndex >= 0) {
        totalsRow[totalIndex] = roundCurrency(totals.total)
      }
    }
    worksheetData.push(totalsRow)

    const commissionPaidRow = createEmptyRow(numColumns)
    if (vinColumnIndex >= 0) {
      commissionPaidRow[vinColumnIndex] = 'COMMISSION PAID:'
    }
    if (totalIndex >= 0) {
      commissionPaidRow[totalIndex] = roundCurrency(advanceCommissionTotal.value)
    }
    worksheetData.push(commissionPaidRow)

    const closingBalanceRow = createEmptyRow(numColumns)
    if (vinColumnIndex >= 0) {
      closingBalanceRow[vinColumnIndex] = 'CLOSING BALANCE:'
    }
    if (totalIndex >= 0) {
      closingBalanceRow[totalIndex] = roundCurrency(commissionClosingBalance.value)
    }
    worksheetData.push(closingBalanceRow)
  }

  if (props.type === 'Performance Bonus Monthly' && exportData.length > 0) {
    const totals = trackerMonthlyTotals.value
    const numColumns = headers.length

    const codeIndex = getColumnIndexInHeaders('code', 0)
    const volumeIndex = getColumnIndexInHeaders('used_funded_volume_total', 0)
    const casesIndex = getColumnIndexInHeaders('used_selected_cases', 0)

    const totalsRow = createEmptyRow(numColumns)
    if (codeIndex >= 0) {
      totalsRow[codeIndex] = 'TOTALS'
    }
    if (volumeIndex >= 0) {
      totalsRow[volumeIndex] = roundCurrency(totals.volume)
    }
    if (casesIndex >= 0) {
      totalsRow[casesIndex] = totals.cases
    }
    worksheetData.push(totalsRow)
  }

  const workbook = XLSX.utils.book_new()
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1')

  const columnMapping: Array<{
    column: GridColumn | null
    isTotal: boolean
    isCreditDebit: boolean
  }> = []
  let excelColIndex = 0

  if (isGroupCommission.value) {
    columnMapping[excelColIndex] = { column: null, isTotal: false, isCreditDebit: false }
    excelColIndex++
  }

  for (const col of visibleColumns.value) {
    if (
      Array.isArray(col.field) &&
      col.field.includes('total') &&
      col.field.includes('debit_credit')
    ) {
      columnMapping[excelColIndex] = { column: col, isTotal: true, isCreditDebit: false }
      excelColIndex++
      columnMapping[excelColIndex] = { column: null, isTotal: false, isCreditDebit: true }
      excelColIndex++
    } else {
      columnMapping[excelColIndex] = { column: col, isTotal: false, isCreditDebit: false }
      excelColIndex++
    }
  }

  for (let R = 1; R <= range.e.r; R++) {
    for (let C = 0; C <= range.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
      if (!worksheet[cellAddress]) continue

      const cell = worksheet[cellAddress]
      if (typeof cell.v !== 'number') continue

      const colMapping = columnMapping[C]

      if (props.type === 'Commission' && colMapping && colMapping.isTotal) {
        cell.z = '£#,##0.00'
        continue
      }

      if (colMapping && colMapping.column && colMapping.column.type === 'currency') {
        cell.z = '£#,##0.00'
      }

      if (colMapping && colMapping.column && colMapping.column.type === 'percent') {
        cell.z = '0.00%'
      }
    }
  }

  const allRowsForWidth =
    props.type === 'Commission' && exportData.length > 0
      ? [...rows, ...worksheetData.slice(rows.length + 1)]
      : rows
  const colWidths = headers.map((header, index) => {
    const maxLength = Math.max(
      header.length,
      ...allRowsForWidth.map((row) => {
        const value = row[index]
        return value ? String(value).length : 0
      }),
    )
    return { wch: Math.min(Math.max(maxLength + 2, 10), 50) }
  })
  worksheet['!cols'] = colWidths

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

  // Extra worksheets only for Tracker (group-level) exports
  if (props.enableTrackerExtras && props.scope === 'Group') {
    try {
      const dateRange = getDateRangeForExport()
      const basePayload = {
        scope: props.scope,
        group: props.group,
        year: filteredYear.value,
      }

      const financedDeals = await axios
        .post('/api/data', {
          ...basePayload,
          type: 'Financed Deals',
          date_from: dateRange.dateFrom ?? undefined,
          date_to: dateRange.dateTo ?? undefined,
        })
        .then((res) => res.data as Record<string, unknown>[])

      const acceptedNotFunded = await axios
        .post('/api/data', {
          ...basePayload,
          type: 'Accepted Not Funded Cases',
          date_from: dateRange.dateFrom ?? undefined,
          date_to: dateRange.dateTo ?? undefined,
        })
        .then((res) => res.data as Record<string, unknown>[])

      const financedSheet = buildWorksheetFromRecords(financedDeals, 'FinancedDeals')
      if (financedSheet) {
        XLSX.utils.book_append_sheet(workbook, financedSheet.sheet, financedSheet.sheetName)
      }
      const acceptedSheet = buildWorksheetFromRecords(acceptedNotFunded, 'AcceptedNotFunded')
      if (acceptedSheet) {
        XLSX.utils.book_append_sheet(workbook, acceptedSheet.sheet, acceptedSheet.sheetName)
      }
    } catch (err) {
      console.error('Extra worksheet fetch failed', err)
    }
  }

  if (props.type === 'Performance Bonus Monthly' && props.scope === 'Group') {
    try {
      const monthArray = Array.isArray(filteredMonth.value)
        ? filteredMonth.value
        : [filteredMonth.value]
      const monthlyDrilldownPayload: Record<string, unknown> = {
        type: 'Performance Bonus Monthly Drilldown',
        scope: props.scope,
        group: props.group,
        include_proposed_not_funded: props.includeProposedNotFunded,
      }
      const selectedPeriods = monthArray.filter((month): month is string => Boolean(month))
      if (selectedPeriods.length > 0) {
        monthlyDrilldownPayload.period = selectedPeriods
      }

      const monthlyDrilldown = await axios
        .post('/api/data', monthlyDrilldownPayload)
        .then((res) => res.data as Record<string, unknown>[])

      const monthlyDrilldownSheet = buildWorksheetFromRecords(monthlyDrilldown, 'MonthlyDrilldown')
      if (monthlyDrilldownSheet) {
        XLSX.utils.book_append_sheet(
          workbook,
          monthlyDrilldownSheet.sheet,
          monthlyDrilldownSheet.sheetName,
        )
      }
    } catch (err) {
      console.error('Monthly drilldown worksheet fetch failed', err)
    }
  }

  const filename = `${getExportFilenamePrefix()}${props.type}_report_${new Date().toISOString().split('T')[0]}.xlsx`

  XLSX.writeFile(workbook, filename)
  exportGenerating.value = false
}

const columnVisibilityButton = ref()
const columnVisibilityPopover = ref()
const columnVisibility = ref<Record<string, boolean>>({})
const columnSearchQuery = ref('')

const getColumnKey = (column: GridColumn): string => {
  const field = Array.isArray(column.field) ? column.field[0] : column.field
  return `${field}_${column.header}`
}

const initializeColumnVisibility = () => {
  if (!props.config?.columns) return

  const visibility: Record<string, boolean> = {}
  props.config.columns.forEach((column: GridColumn) => {
    const key = getColumnKey(column)
    visibility[key] = true
  })
  columnVisibility.value = visibility
}

const isGroupCommission = computed(() => {
  return props.type === 'Commission' && props.scope === 'Group'
})

const isTotalDebitCreditField = (column: GridColumn): boolean => {
  return (
    Array.isArray(column.field) &&
    column.field.includes('total') &&
    column.field.includes('debit_credit')
  )
}

const vatNotesColumns = computed(() => {
  if (useNewVatNotesFormat.value) {
    return [
      { field: 'period', header: 'Period', type: 'text' },
      { field: 'pfl_exc_vat', header: 'Finance Lease EXC VAT', type: 'currency' },
      { field: 'pfl_vat', header: 'Finance Lease VAT', type: 'currency' },
      { field: 'ch_exc_vat', header: 'Contract Hire EXC VAT', type: 'currency' },
      { field: 'ch_vat', header: 'Contract Hire VAT', type: 'currency' },
      { field: 'total_vat', header: 'Grand Total', type: 'currency' },
    ]
  }

  return [
    { field: 'period', header: 'Period', type: 'text' },
    { field: 'pfl_exc_vat', header: 'PFL EXC VAT', type: 'currency' },
    { field: 'pfl_vat', header: 'PFL VAT', type: 'currency' },
    { field: 'ch_exc_vat', header: 'CH EXC VAT', type: 'currency' },
    { field: 'ch_vat', header: 'CH VAT', type: 'currency' },
    { field: 'ic_pcp_exc_vat', header: 'IC & PCP EXC VAT', type: 'currency' },
    { field: 'ic_pcp_vat', header: 'IC & PCP VAT', type: 'currency' },
    { field: 'total_exc_vat', header: 'Total exc VAT', type: 'currency' },
    { field: 'total_vat', header: 'Total VAT', type: 'currency' },
  ]
})

const visibleColumns = computed(() => {
  if (props.type === 'VAT Notes' && statementExportVisible.value) {
    return vatNotesColumns.value
  }

  if (!props.config?.columns) return []

  const columns = props.config.columns.filter((column: GridColumn) => {
    const key = getColumnKey(column)
    return columnVisibility.value[key] !== false
  })

  if (isGroupCommission.value && statementExportVisible.value) {
    const retailerColumn: GridColumn = {
      field: 'sub_dealer_code',
      header: 'Retailer',
      type: 'text',
    }
    return [retailerColumn, ...columns]
  }

  return columns
})

// Mobile pagination computed properties
const mobileTotalPages = computed(() => {
  if (!filteredData.value) return 0
  return Math.ceil(filteredData.value.length / mobileRowsPerPage)
})

const mobilePageStart = computed(() => {
  return (mobileCurrentPage.value - 1) * mobileRowsPerPage + 1
})

const mobilePageEnd = computed(() => {
  const end = mobileCurrentPage.value * mobileRowsPerPage
  return Math.min(end, filteredData.value?.length || 0)
})

const paginatedMobileData = computed(() => {
  if (!filteredData.value) return []
  const start = (mobileCurrentPage.value - 1) * mobileRowsPerPage
  const end = start + mobileRowsPerPage
  return filteredData.value.slice(start, end)
})

// Mobile card view helper methods
const getRowId = (row: Record<string, unknown>): string | number => {
  if (row.id) return row.id as string | number
  if (props.dataKey && row[props.dataKey]) return row[props.dataKey] as string | number
  return JSON.stringify(row)
}

const toggleCardExpansion = (id: string | number) => {
  const index = expandedCardIds.value.indexOf(id)
  if (index > -1) {
    expandedCardIds.value.splice(index, 1)
  } else {
    expandedCardIds.value.push(id)
  }
}

const getPrimaryFieldValue = (row: Record<string, unknown>): string => {
  if (!visibleColumns.value.length) return 'N/A'

  // Priority order for primary columns (field names and headers)
  const primaryFieldPatterns: Array<{
    field?: string
    header?: RegExp
  }> = [
    // Customer-related columns
    { field: 'customer', header: /^Customer/ },
    { field: 'customer_name', header: /^Customer/ },
    { field: 'surname', header: /^Insured/ },
    // Contract/Agreement columns
    { field: 'agreement_number', header: /^(Contract|Agreement Number)/ },
    { field: 'agreement_reference', header: /^Contract/ },
  ]

  // Search for primary column in priority order
  for (const pattern of primaryFieldPatterns) {
    const column = visibleColumns.value.find((col: GridColumn) => {
      const fieldMatch =
        pattern.field && typeof col.field === 'string' && col.field === pattern.field
      const headerMatch = pattern.header && pattern.header.test(col.header)
      return fieldMatch || headerMatch
    })

    if (column) {
      const value = formatCellValueForMobile(row, column)
      if (value && value.trim()) return value
    }
  }

  // Fallback to first column if no primary column found
  const firstColumn = visibleColumns.value[0]
  if (firstColumn) {
    const value = formatCellValueForMobile(row, firstColumn)
    return value && value.trim() ? value : 'N/A'
  }

  return 'N/A'
}

const getKeyMetrics = (
  row: Record<string, unknown>,
): Array<{ label: string; value: string; class: string }> => {
  const metrics: Array<{ label: string; value: string; class: string }> = []

  // Find currency column
  const currencyCol = visibleColumns.value.find((col: GridColumn) => col.type === 'currency')
  if (currencyCol) {
    const value = row[getColumnField(currencyCol)]
    metrics.push({
      label: currencyCol.header,
      value: formatCellValueForMobile(row, currencyCol),
      class: typeof value === 'number' && value < 0 ? 'text-red-600' : 'text-green-600',
    })
  }

  // Find date column
  const dateCol = visibleColumns.value.find((col: GridColumn) => col.type === 'date')
  if (dateCol && !metrics.find((m) => m.label === dateCol.header)) {
    metrics.push({
      label: dateCol.header,
      value: formatCellValueForMobile(row, dateCol),
      class: 'text-(--p-anthracite-500)',
    })
  }

  // Add one more text field if available
  if (metrics.length < 2) {
    const textCol = visibleColumns.value.find(
      (col: GridColumn) => col.type === 'text' && !metrics.find((m) => m.label === col.header),
    )
    if (textCol) {
      metrics.push({
        label: textCol.header,
        value: formatCellValueForMobile(row, textCol),
        class: 'text-(--p-anthracite-500)',
      })
    }
  }

  return metrics.slice(0, 3) // Max 3 metrics
}

const formatCellValueForMobile = (row: Record<string, unknown>, column: GridColumn): string => {
  const field = getColumnField(column)

  if (column.type === 'date') {
    return row[field] ? formatDate(row[field] as string) : ''
  }
  if (column.type === 'percent') {
    return row[field] ? `${row[field]}%` : ''
  }
  if (column.type === 'currency') {
    return row[field] ? formatCurrencyValue(row[field] as number) : '£0.00'
  }
  if (Array.isArray(column.field)) {
    return renderArrayFieldValue(row, column.field, column.type)
  }
  if (column.field === 'agreement_reference') {
    return formatAgreementReference(row[field] as string)
  }
  return row[field] ? decodeHtmlEntities(String(row[field])) : ''
}

const getCellClass = (row: Record<string, unknown>, column: GridColumn): string => {
  if (column.type === 'currency') {
    const value = row[getColumnField(column)]
    return typeof value === 'number' && value < 0 ? 'text-red-600 font-semibold' : ''
  }
  return ''
}

const showAllColumns = () => {
  if (!props.config?.columns) return

  props.config.columns.forEach((column: GridColumn) => {
    const key = getColumnKey(column)
    columnVisibility.value[key] = true
  })
}

const hideAllColumns = () => {
  if (!props.config?.columns) return

  props.config.columns.forEach((column: GridColumn) => {
    const key = getColumnKey(column)
    columnVisibility.value[key] = false
  })
}

const filteredColumns = computed(() => {
  if (!props.config?.columns) return []
  if (!columnSearchQuery.value.trim()) return props.config.columns

  const query = columnSearchQuery.value.toLowerCase().trim()
  return props.config.columns.filter((column: GridColumn) =>
    column.header.toLowerCase().includes(query),
  )
})

const visibleColumnsCount = computed(() => {
  return Object.values(columnVisibility.value).filter((v) => v).length
})

const toggleColumnVisibilityPopover = (event: Event) => {
  columnVisibilityPopover.value?.toggle(event)
}

const toggleColumnVisibility = (column: GridColumn) => {
  const key = getColumnKey(column)
  const newValue = !columnVisibility.value[key]
  updateColumnVisibilityForColumn(column, newValue)
}

const updateColumnVisibilityForColumn = (column: GridColumn, value: boolean) => {
  const key = getColumnKey(column)
  columnVisibility.value[key] = value
  updateColumnVisibility()
}

const updateColumnVisibility = () => {
  const visibleCount = Object.values(columnVisibility.value).filter((v) => v).length
  if (visibleCount === 0 && props.config?.columns?.length > 0) {
    const firstColumn = props.config.columns[0]
    const key = getColumnKey(firstColumn)
    columnVisibility.value[key] = true
  }
}

const resetColumnVisibility = () => {
  initializeColumnVisibility()
  columnSearchQuery.value = ''
}
</script>

<style scoped>
:deep(.custom-datagrid .p-datatable-thead > tr > th) {
  background: var(--p-primary-500);
  color: white;
  font-family: 'Encode Sans Expanded', sans-serif;
  font-weight: 600;
  padding: 0.875rem 1rem;
  border: none;
}

:deep(.custom-datagrid .p-datatable-tbody > tr) {
  transition: all 0.15s ease;
}

:deep(.custom-datagrid .p-datatable-tbody > tr:hover) {
  background: var(--p-primary-50) !important;
}

:deep(.custom-datagrid .p-datatable-tbody > tr > td) {
  padding: 0.875rem 1rem;
  border-color: var(--p-surface-200);
}

:deep(.p-datatable-header-cell) {
  .p-datatable-sort-icon {
    color: #fff;
  }

  .p-button-text.p-button-secondary {
    color: #fff !important;
  }

  &:hover {
    .p-datatable-sort-icon {
      color: var(--p-anthracite-500) !important;
    }

    .p-button-text.p-button-secondary {
      background-color: transparent;
      color: var(--p-anthracite-500) !important;
    }
  }
}

.export-dialog :deep(.p-dialog) {
  border-radius: 12px;
}

.statement-export-container {
  font-family: Arial, sans-serif;
  display: block !important;
}

.statement-export-container > .statement-header {
  display: flex !important;
  width: 100% !important;
  box-sizing: border-box !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  padding-left: 1.25rem !important;
  padding-right: 1.25rem !important;
  position: relative !important;
  clear: both !important;
  float: none !important;
  table-layout: auto !important;
}

.statement-table {
  border-collapse: collapse;
}

.statement-table :deep(table) {
  table-layout: auto;
  width: 100%;
}

.statement-table :deep(.p-datatable-thead) {
  display: table-header-group;
}

.statement-table :deep(.p-datatable-thead > tr > th) {
  background: var(--p-primary-500);
  color: white;
  font-family: 'Encode Sans Expanded', sans-serif;
  font-weight: 600;
  padding: 8px 4px;
  border: 1px solid #ddd;
  font-size: 10px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.statement-table :deep(.p-datatable-tbody) {
  display: table-row-group;
}

.statement-table :deep(.p-datatable-tbody > tr > td) {
  padding: 6px 4px;
  border: 1px solid #ddd;
  font-size: 10px;
}

.statement-table :deep(.p-datatable-tbody > tr.totals-row > td) {
  background-color: #f3f4f6;
  font-weight: bold;
}

.statement-table :deep(.p-datatable-tbody > tr.commission-paid-row > td) {
  font-weight: bold;
}

.statement-table :deep(.p-datatable-tbody > tr.closing-balance-row > td) {
  font-weight: bold;
}

.statement-table :deep(.p-datatable-tbody > tr.subtotal-row > td) {
  background-color: #f9fafb;
  font-weight: bold;
}

.statement-export-container .text-red-600 {
  color: #dc2626 !important;
}

.statement-export-container.maia-recharge .text-red-600 {
  color: #000 !important;
}

.statement-table :deep(.pdf-repeated-header) {
  display: table-row !important;
}

:deep(.p-datatable-column-title) {
  font-weight: 600 !important;
}

.statement-table :deep(.pdf-repeated-header > th) {
  background: var(--p-primary-500) !important;
  color: white !important;
  font-family: 'Encode Sans Expanded', sans-serif !important;
  font-weight: 600 !important;
  padding: 8px !important;
  border: 1px solid #ddd !important;
  font-size: 12px !important;
  display: table-cell !important;
}

.pdf-repeated-page-header-row {
  display: table-row !important;
  page-break-before: always;
  break-before: page;
  width: 100% !important;
}

.pdf-repeated-page-header-row td {
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  height: 0 !important;
  line-height: 0 !important;
  font-size: 0 !important;
  overflow: visible !important;
  position: relative !important;
}

.pdf-repeated-page-header-div {
  display: block !important;
  width: 100% !important;
  margin-right: 0 !important;
  padding: 0 !important;
  box-sizing: border-box !important;
}

.pdf-repeated-page-header {
  display: flex !important;
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  margin-bottom: 15px !important;
  justify-content: space-between !important;
  align-items: center !important;
  box-sizing: border-box !important;
  position: relative !important;
}

.pdf-repeated-retailer-info-row {
  page-break-after: avoid;
  break-after: avoid;
}

.pdf-repeated-retailer-info-row td {
  padding: 0 !important;
  border: none !important;
  display: block !important;
}

.pdf-repeated-retailer-info {
  display: block !important;
  width: 100% !important;
  margin-bottom: 0 !important;
}

.pdf-repeated-footer-row {
  page-break-after: always;
  break-after: page;
}

.pdf-repeated-footer-row td {
  padding: 0 !important;
  border: none !important;
}

.vat-notes-retailer-info {
  display: flex;
  gap: 2rem;
}

.vat-notes-retailer-info > div {
  flex: 1;
}

@media print {
  .vat-notes-retailer-info {
    display: flex !important;
    gap: 2rem !important;
    margin-bottom: 1rem !important;
  }

  .vat-notes-retailer-info > div {
    flex: 1 !important;
  }
}

.pdf-repeated-footer {
  margin-top: 10px;
  padding: 15px 10px;
  border-top: 2px solid #333;
  font-size: 11px;
  display: block;
  width: 100%;
}

@media print {
  .statement-export-container {
    display: block !important;
    page-break-inside: avoid;
  }

  .statement-export-container > .statement-header {
    display: flex !important;
    width: 100% !important;
    box-sizing: border-box !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 1.25rem !important;
    padding-right: 1.25rem !important;
    position: relative !important;
    clear: both !important;
    float: none !important;
    table-layout: auto !important;
    page-break-after: avoid;
  }

  .statement-table {
    page-break-inside: auto;
  }

  .pdf-repeated-page-header-row {
    display: table-row !important;
    page-break-before: always !important;
    break-before: page !important;
    page-break-inside: avoid !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .pdf-repeated-page-header-row td {
    display: table-cell !important;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    height: 0 !important;
    line-height: 0 !important;
    font-size: 0 !important;
    overflow: visible !important;
    position: relative !important;
    width: 0 !important;
    min-width: 0 !important;
  }

  .pdf-repeated-page-header-div {
    display: block !important;
    width: 100% !important;
    margin-right: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
  }

  .pdf-repeated-page-header {
    display: flex !important;
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 1.25rem !important;
    padding-right: 1.25rem !important;
    position: relative !important;
    clear: both !important;
    float: none !important;
    page-break-inside: avoid !important;
    page-break-after: avoid !important;
    margin-bottom: 15px !important;
    justify-content: space-between !important;
    align-items: center !important;
  }

  .pdf-repeated-retailer-info-row {
    page-break-inside: avoid !important;
    page-break-after: avoid !important;
  }

  .pdf-repeated-retailer-info {
    page-break-inside: avoid !important;
    page-break-after: avoid !important;
  }

  .statement-table :deep(.p-datatable-thead) {
    display: table-header-group !important;
  }

  .statement-table :deep(.p-datatable-thead > tr) {
    display: table-row !important;
    page-break-inside: avoid !important;
    page-break-after: avoid !important;
  }

  .statement-table :deep(.p-datatable-thead > tr > th) {
    display: table-cell !important;
    page-break-inside: avoid !important;
    page-break-after: avoid !important;
  }

  .statement-table :deep(table) {
    border-collapse: collapse !important;
    width: 100% !important;
    table-layout: auto !important;
  }

  .statement-table :deep(.p-datatable-tbody) {
    display: table-row-group;
  }

  .statement-table :deep(.p-datatable-tbody > tr) {
    page-break-inside: avoid;
    page-break-after: auto;
  }

  .statement-table :deep(.p-datatable-tbody > tr > td) {
    page-break-inside: avoid;
  }

  .totals-section {
    page-break-inside: avoid;
  }

  .statement-footer {
    page-break-before: avoid;
  }
}

.dark-selector .datagrid-container {
  background: var(--p-surface-900);
  border: 1px solid var(--p-surface-700);
}

.a11y-reduced-motion :deep(.custom-datagrid .p-datatable-tbody > tr) {
  transition: none !important;
}

.a11y-high-contrast .datagrid-container {
  border: 2px solid var(--p-primary-500);
}

:deep(.column-visibility-popover) {
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid var(--p-surface-200);
  border-radius: 12px;
  padding: 0.75rem;
}

:deep(.column-visibility-popover .p-popover-content) {
  padding: 0;
}

.column-visibility-popover .flex.flex-col.gap-1::-webkit-scrollbar {
  width: 6px;
}

.column-visibility-popover .flex.flex-col.gap-1::-webkit-scrollbar-track {
  background: var(--p-surface-100);
  border-radius: 3px;
}

.column-visibility-popover .flex.flex-col.gap-1::-webkit-scrollbar-thumb {
  background: var(--p-surface-300);
  border-radius: 3px;
}

.column-visibility-popover .flex.flex-col.gap-1::-webkit-scrollbar-thumb:hover {
  background: var(--p-surface-400);
}

.pdf-export-mode,
.pdf-export-mode * {
  color-scheme: light;
}

.pdf-export-mode [style*='oklch'],
.pdf-export-mode [style*='var(--'] {
  will-change: auto;
}

/* Improved pagination styling for mobile */
/* Mobile Card View Styles */
.mobile-datagrid-wrapper {
  padding: 0.75rem;
}

.mobile-card {
  transition: box-shadow 0.2s ease;
}

.mobile-card.expanded {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.card-header {
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.metric-item {
  min-width: 0;
}

.detail-row {
  min-height: 2.5rem;
}

.detail-label {
  line-height: 1.4;
}

.detail-value {
  line-height: 1.4;
  word-break: break-word;
}

/* Slide down animation */
.slide-down-enter-active {
  transition: all 0.2s ease;
}

.slide-down-leave-active {
  transition: all 0.2s ease;
}

.slide-down-enter-from {
  opacity: 0;
  max-height: 0;
}

.slide-down-enter-to {
  opacity: 1;
  max-height: 1000px;
}

.slide-down-leave-from {
  opacity: 1;
  max-height: 1000px;
}

.slide-down-leave-to {
  opacity: 0;
  max-height: 0;
}

/* Touch-friendly active states */
@media (hover: none) and (pointer: coarse) {
  .card-header:active {
    background-color: var(--p-surface-50);
  }
}

@media (max-width: 639px) {
  :deep(.custom-datagrid .p-paginator) {
    padding: 0.5rem !important;
    flex-wrap: wrap !important;
    gap: 0.5rem !important;
  }

  :deep(.custom-datagrid .p-paginator .p-paginator-left-content) {
    width: 100% !important;
    margin-bottom: 0.5rem !important;
    order: 1 !important;
  }

  :deep(.custom-datagrid .p-paginator .p-paginator-right-content) {
    width: 100% !important;
    display: flex !important;
    flex-wrap: wrap !important;
    justify-content: center !important;
    gap: 0.25rem !important;
    order: 2 !important;
  }

  :deep(.custom-datagrid .p-paginator .p-paginator-pages) {
    display: none !important;
  }

  :deep(.custom-datagrid .p-paginator .p-paginator-first),
  :deep(.custom-datagrid .p-paginator .p-paginator-last) {
    display: none !important;
  }

  :deep(.custom-datagrid .p-paginator .p-paginator-prev),
  :deep(.custom-datagrid .p-paginator .p-paginator-next) {
    min-width: 44px !important;
    height: 44px !important;
    padding: 0.5rem !important;
    font-size: 1rem !important;
  }

  :deep(.custom-datagrid .p-paginator .p-paginator-current) {
    font-size: 0.75rem !important;
    padding: 0.5rem 0.75rem !important;
    text-align: center !important;
    width: 100% !important;
    order: 3 !important;
    margin-top: 0.5rem !important;
  }

  :deep(.custom-datagrid .p-paginator .p-dropdown) {
    min-width: 80px !important;
    height: 44px !important;
  }

  :deep(.custom-datagrid .p-paginator button) {
    min-width: 44px !important;
    min-height: 44px !important;
    padding: 0.5rem !important;
    font-size: 0.875rem !important;
  }

  :deep(.custom-datagrid .p-paginator .p-paginator-rpp-options) {
    min-width: 80px !important;
  }
}

@media (min-width: 640px) and (max-width: 1023px) {
  :deep(.custom-datagrid .p-paginator) {
    padding: 0.75rem 1rem !important;
    flex-wrap: wrap !important;
    gap: 0.5rem !important;
  }

  :deep(.custom-datagrid .p-paginator .p-paginator-current) {
    font-size: 0.8125rem !important;
    padding: 0.5rem 1rem !important;
  }

  :deep(.custom-datagrid .p-paginator button) {
    min-width: 40px !important;
    min-height: 40px !important;
    padding: 0.5rem !important;
  }
}
</style>
